import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import AIResponseSource, {
  applySourceSelectionToMap,
  formatSupportRoundDate,
} from "./AIResponseSource";
import GlobalOverviewResponse from "./GlobalOverviewResponse";
import TrendLineChart from "./analysis/TrendLineChart";
import GenericQueryResultVisualization from "./visualization/GenericQueryResultVisualization";
import { getVisualizationEligibility } from "./visualization/utils/getVisualizationEligibility";
import { getDataFreshness } from "../util/dataFreshness";
import { sanitizeHtml } from "../util/sanitizeHtml";
import { getIso3ByCountryName } from "../util/countryLookup";
import {
  getQueryResultColumnKeys,
  getQueryResultColumnLabels,
} from "../util/queryResultColumns";
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import ReportIssueModal from "./ReportIssueModal";
import { apiUrl } from "../configs/api";
import classes from "./AIResponse.module.css";
import overviewClasses from "./GlobalOverviewResponse.module.css";

const getResponseDataRows = (response) => {
  if (Array.isArray(response?.data) && response.data.length > 0) {
    return response.data;
  }

  if (Array.isArray(response?.query_result)) {
    return response.query_result;
  }

  return Array.isArray(response?.data) ? response.data : [];
};


const isExplicitRoundResponse = (response) => {
  const roundScope = response?.round_scope;

  return roundScope === "last_n_rounds" || roundScope === "specific_round";
};

const getExplicitRoundSelectedRounds = (response) => {
  const sources = [
    response?.selected_rounds,
    response?.selected_round_provenance,
    response?.debug?.explicit_round_selection?.selected_rounds,
  ];

  return sources.find(Array.isArray) || [];
};

const getExplicitRoundResultRows = (response) => {
  const sources = [
    response?.result_rows,
    response?.query_result,
    response?.raw_rows,
    response?.selected_rows,
  ];

  return sources.find(Array.isArray) || [];
};
const getExplicitRoundMissingRequestedRounds = (response) => {
  const value =
    response?.missing_requested_rounds ??
    response?.round_selection_metadata?.missing_requested_rounds;

  if (Array.isArray(value)) return value;
  return value === null || value === undefined || value === "" ? [] : [value];
};

const formatExplicitRoundDate = (value) => {
  if (value === null || value === undefined || value === "") return "?";
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
};

const formatMissingRound = (value) => {
  const round = value?.round_number ?? value?.round ?? value;
  const text = String(round);
  return text.startsWith("R") ? text : `R${text}`;
};

const formatExplicitRoundValue = (value) => {
  if (value === null || value === undefined || value === "") return "?";
  if (typeof value === "number") return value.toLocaleString("en-US");
  return String(value);
};

const ExplicitRoundSelectedRoundsTable = ({ rows }) => {
  if (!rows.length) {
    return <p>No selected rounds were returned.</p>;
  }

  return (
    <div className={classes.table_wrap}>
      <table className={classes.table} aria-label="Selected rounds">
        <thead>
          <tr>
            <th>Country</th>
            <th>Operation</th>
            <th>Round</th>
            <th>Reporting date</th>
            <th>Year</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const round = row?.round_number ?? row?.round ?? row?.latest_round;

            return (
              <tr key={row?.RowId ?? row?.id ?? index}>
                <td>{formatExplicitRoundValue(row?.admin_0_name ?? row?.country)}</td>
                <td>{formatExplicitRoundValue(row?.operation)}</td>
                <td>{round === null || round === undefined ? "?" : `R${round}`}</td>
                <td>{formatExplicitRoundDate(row?.reporting_date ?? row?.reportingDate)}</td>
                <td>{String(row?.reporting_year ?? row?.year ?? "?")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
const getVisualizationSpec = (response) =>
  response?.visualization || response?.summary?.visualization_spec || null;

const parseJsonValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getOperationItems = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) return parsedValue;
  if (!parsedValue) return [];
  if (Array.isArray(parsedValue.operations)) return parsedValue.operations;
  if (Array.isArray(parsedValue.rounds)) return parsedValue.rounds;
  if (Array.isArray(parsedValue.data)) return parsedValue.data;

  return [parsedValue];
};

const OPERATION_BREAKDOWN_COLUMN = "__operation_breakdown";
const OPERATION_BREAKDOWN_LABEL = "Operation breakdown";
const OPERATION_BREAKDOWN_COLUMN_ORDER = [
  "operation",
  "round",
  "reporting date",
  "freshness",
  "Total idp",
  "female idps",
  "male idps",
];
const OPERATION_BREAKDOWN_LABELS = {
  o: "operation",
  operation: "operation",
  r: "round",
  round: "round",
  round_number: "round",
  d: "reporting date",
  reporting_date: "reporting date",
  fr: "freshness",
  freshness: "freshness",
  round_freshness: "freshness",
  t: "Total idp",
  total_idps: "Total idp",
  total: "Total idp",
  idps: "Total idp",
  f: "female idps",
  female: "female idps",
  female_idps: "female idps",
  m: "male idps",
  male: "male idps",
  male_idps: "male idps",
};

const normalizeOperationBreakdownKey = (key) =>
  OPERATION_BREAKDOWN_LABELS[String(key || "").trim()] ||
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasOperationBreakdown = (row) =>
  getOperationItems(row?.operation_data).length > 0;

const normalizeOperationBreakdownRow = (item) => {
  const operation = parseJsonValue(item) || {};

  return Object.entries(operation).reduce((normalized, [key, value]) => {
    const label = normalizeOperationBreakdownKey(key);
    if (!label) return normalized;
    if (normalized[label] === undefined || normalized[label] === null || normalized[label] === "") {
      normalized[label] = value;
    }
    return normalized;
  }, {});
};

const getOperationBreakdownRows = (row) =>
  getOperationItems(row?.operation_data)
    .map(normalizeOperationBreakdownRow)
    .filter((item) => Object.keys(item).length > 0);

const getOperationBreakdownColumns = (rows) => {
  const availableColumns = rows.reduce((columns, row) => {
    Object.keys(row).forEach((column) => columns.add(column));
    return columns;
  }, new Set());

  return [
    ...OPERATION_BREAKDOWN_COLUMN_ORDER.filter((column) =>
      availableColumns.has(column),
    ),
    ...Array.from(availableColumns).filter(
      (column) => !OPERATION_BREAKDOWN_COLUMN_ORDER.includes(column),
    ),
  ];
};

const extractOperationMeta = (row) => {
  const first = getOperationItems(row?.operation_data)[0];
  const operation = parseJsonValue(first) || {};

  return {
    round: operation.r ?? operation.round ?? operation.round_number ?? null,
    iso3: operation.iso3 ?? operation.admin_0_pcode ?? null,
    operation: operation.o ?? operation.operation ?? null,
  };
};

const getResponseStatus = (response) => response?.response_status || null;

const getStatusTitle = (status) => status?.title || "";

const getStatusBody = (response, status) =>
  status?.body ||
  status?.content ||
  status?.message ||
  response?.summary?.summary_text ||
  (typeof response?.summary === "string" ? response.summary : "") ||
  response?.message ||
  "";

const getStatusType = (status) => {
  const allowed = ["normal", "info", "warning", "error"];
  return allowed.includes(status?.type) ? status.type : "normal";
};

const shouldHideVisualizationTab = (response) =>
  !getVisualizationEligibility(response).eligible;

const HIDE_CHROME_STATUS_VALUES = new Set([
  "NOT_MAPPED",
  "INVALID_QUERY_VALUES",
]);

const getNoDataAdvisory = (response) =>
  response?.NoDataAdvisory ||
  response?.noDataAdvisory ||
  response?.no_data_advisory ||
  response?.NoDataAdvisor ||
  response?.noDataAdvisor ||
  response?.no_data_advisor ||
  response?.dataAdvisory ||
  response?.data_advisory ||
  response?.advisory ||
  null;

const hasNoDataAdvisory = (response) => {
  const advisory = getNoDataAdvisory(response);

  if (!advisory) return false;

  return Boolean(
    advisory.title ||
      advisory.message ||
      advisory.body ||
      advisory.content ||
      advisory.summary ||
      advisory.recommendation ||
      advisory.suggested_prompt ||
      advisory.suggested_prompts,
  );
};

const getResponseSummaryText = (response) =>
  response?.summary_text ||
  response?.summary?.summary_text ||
  (typeof response?.summary === "string" ? response.summary : "");

const isSpecificRoundSnapshotNoData = (response) => {
  const summary =
    response?.summary && typeof response.summary === "object"
      ? response.summary
      : null;
  const summaryType = response?.summary_type ?? summary?.summary_type;
  const summaryVariant = response?.summary_variant ?? summary?.summary_variant;
  const summaryStatus = response?.summary_status ?? summary?.summary_status;

  return (
    String(summaryType || "").toLowerCase() ===
      "specific_round_snapshot_no_data" ||
    (String(summaryStatus || "").toLowerCase() === "no_data" &&
      String(summaryVariant || "").toLowerCase() === "warning")
  );
};

const hasDataGapAdvisory = (response) =>
  response?.summary_source === "data_gap_advisory" ||
  response?.summary_status === "no_data_with_advisory" ||
  response?.data_gap_advisory?.advisory_type === "data_gap";

const getDataGapMessage = (response) => {
  const advisory = response?.data_gap_advisory;

  if (!advisory) return getResponseSummaryText(response);

  if (advisory.full_message) return advisory.full_message;
  if (advisory.message) return advisory.message;

  const summaryText = getResponseSummaryText(response);
  if (summaryText) return summaryText;

  const primary = advisory.primary_message || "";
  const detail = advisory.detail_message || "";

  return [primary, detail].filter(Boolean).join(" ");
};

const getDataGapSuggestions = (response) => {
  const suggestions = response?.data_gap_advisory?.suggestions;
  if (!Array.isArray(suggestions)) return [];

  return suggestions
    .filter((suggestion) => suggestion?.label && suggestion?.query_text)
    .slice(0, 2);
};
const getRowIdentifierValues = (row, index) =>
  [
    row?.RowId,
    row?.row_id,
    row?.rowId,
    row?.id,
    row?.ID,
    row?.rank,
    row?.Rank,
    Number.isInteger(index) ? index + 1 : null,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value));

const SUMMARY_SUPPORT_SECTION_KEYS = [
  "overview",
  "reason",
  "location",
  "key_figures",
];

const getSummarySupportSectionEntries = (summarySupportTables) =>
  SUMMARY_SUPPORT_SECTION_KEYS.map((section) => {
    const table = summarySupportTables?.[section];
    const rows = Array.isArray(table?.rows) ? table.rows : [];

    return {
      section,
      sectionTitle: table?.title || section.replace(/_/g, " "),
      rows,
    };
  });

// A round-country fallback derived ONLY from data intrinsic to the CURRENT
// response (query_result, then any already-explicit country already present
// on one of this response's own summary_support rounds). Deliberately never
// reads the globally-selected map country (Redux state.selection.country):
// that selection is mutable UI state that outlives any single response —
// e.g. it's set by clicking a Data Sources entry — so using it as a
// fallback let a PREVIOUS response's/interaction's country leak into a
// later, unrelated response whenever that response's own rows omitted
// row-level country. Scanning this response's own rounds instead guarantees
// the fallback always tracks the response actually being rendered.
const getResponseCountryFallback = (summarySupportTables, queryResult) => {
  const fromQueryResult = queryResult?.[0]?.admin_0_name || queryResult?.[0]?.country || "";
  if (fromQueryResult) return fromQueryResult;

  for (const entry of getSummarySupportSectionEntries(summarySupportTables)) {
    for (const row of entry.rows) {
      const rounds = normalizeSupportOperationRounds(row?.source_operation_rounds);
      const roundWithCountry = rounds.find((round) => round?.country);
      if (roundWithCountry) return roundWithCountry.country;
    }
  }

  return "";
};

const resolveSummarySupportRow = (summarySupportTables, summaryRowId) => {
  if (!summaryRowId) return null;

  const matches = [];
  for (const entry of getSummarySupportSectionEntries(summarySupportTables)) {
    const row = entry.rows.find((item) => String(item?.id) === String(summaryRowId));
    if (row) {
      matches.push({
        section: entry.section,
        sectionTitle: entry.sectionTitle,
        row,
      });
    }
  }

  return matches.length === 1 ? matches[0] : null;
};

const normalizeSupportText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isCalculatedSummarySupportRow = (row) => {
  if (!row) return false;
  if (row.source_clickable === false || row.is_calculated === true) return true;

  const text = normalizeSupportText(
    [row.id, row.label, row.method, row.dimension, row.section]
      .filter(Boolean)
      .join(" "),
  );

  return /\baverage\b|\bavg\b|\bkey ?figures? ?3\b/.test(text);
};

const getCitationRowId = (node) =>
  node.getAttribute("data-data-row-id") ||
  node.getAttribute("data-row-id") ||
  node.getAttribute("data-source-row-id") ||
  node.getAttribute("data-query-row-id") ||
  node.getAttribute("data-rank");

const getCitationTargetRowId = (node) =>
  node?.getAttribute?.("data-summary-row-id") || getCitationRowId(node);

// Same field-resolution priority as AIResponseSource.js's getDetailItems, so the
// provenance key matches exactly what the Data Sources panel displays for this row.
const getQueryResultOperationDetails = (row) => {
  const operationData = getOperationItems(row?.operation_data);
  const operationDetail = getOperationItems(row?.operation_detail);
  const items = operationData.length ? operationData : operationDetail;

  return items.length ? items.map((item) => parseJsonValue(item) || {}) : [{}];
};

// Datasource provenance identity for a query_result row: country + operation + round +
// reporting_date, resolved from operation_data/operation_detail (falling back to
// row-level fields), mirroring getDetailItems. Rows citing the same underlying
// operation/round/date (e.g. an admin1 breakdown of one round) must share one citation
// number instead of minting a new one per row.
const getQueryResultProvenanceKey = (row) => {
  if (!row) return null;

  const details = getQueryResultOperationDetails(row).map((detail) => ({
    country:
      detail.country ||
      detail.admin_0_name ||
      detail.c ||
      row?.admin_0_name ||
      row?.country ||
      row?.iso3 ||
      "",
    operation: detail.operation || detail.o || row?.operation || "",
    round:
      detail.round ||
      detail.round_number ||
      detail.r ||
      row?.round ||
      row?.round_number ||
      "",
    date:
      detail.reporting_date ||
      detail.date ||
      detail.d ||
      row?.reporting_date ||
      "",
  }));

  const hasIdentity = details.some(
    (detail) => detail.country || detail.operation || detail.round || detail.date,
  );
  if (!hasIdentity) return null;

  return details
    .map((detail) => [detail.country, detail.operation, detail.round, detail.date].join("|"))
    .join(";");
};

// A support row's country may be absent on the round itself even though the
// response is clearly about a known country (e.g. the user's selected
// country). Falling back to that known country — instead of leaving the
// identity blank or rendering "Unknown country" — keeps a round that's
// really the same source from being canonicalized as a distinct one.
const resolveSummarySupportRoundCountry = (round, fallbackCountry) =>
  round?.country || fallbackCountry || "";

// Normalizes a free-text provenance field (country/operation) so incidental
// whitespace/casing differences between round objects supplied by different
// backend generation paths for the SAME response (e.g. an Overview support
// row vs. a Key Figures/current_year_stats support row) don't make one
// real-world provenance source look like two distinct ones.
const normalizeProvenanceText = (value) =>
  String(value || "").replace(/\s+/g, " ").trim();

// The reporting date on a round can arrive as either an already-formatted
// display string ("Aug 31 2024") or a raw/ISO value ("2024-08-31")
// depending on which backend path produced that particular support row for
// the SAME underlying round — both render identically once passed through
// AIResponseSource.js's display formatting, but as raw text they are
// different strings. The canonical provenance KEY must collapse a single
// (non-range) date to one form before comparing, or two rows citing the
// identical round go through this different-format path and mint two
// datasource numbers for one real source. Range strings ("Jan 1 - Feb 1", a
// comma-joined list) aren't single dates, so they're left whitespace-
// normalized only. Mirrors the existing UTC-based normalization already
// used for table cells (see formatReportingDate) for consistency.
const normalizeProvenanceDate = (value) => {
  const text = normalizeProvenanceText(value);
  if (!text || text.includes(",") || text.includes(" to ")) return text;

  // A bare ISO date ("2024-08-31") is parsed as UTC per spec, but any other
  // format ("Aug 31 2024") is parsed in the LOCAL timezone by `new Date()`.
  // Anchoring non-ISO text to UTC explicitly keeps both formats resolving
  // to the same calendar date whenever the host machine isn't in UTC —
  // otherwise the identical real-world date would canonicalize to two
  // different keys depending only on which backend path formatted it.
  const isIsoDate = /^\d{4}-\d{2}-\d{2}/.test(text);
  const parsed = new Date(isIsoDate ? text : `${text} UTC`);
  if (Number.isNaN(parsed.getTime())) return text;

  return [
    parsed.getUTCFullYear(),
    String(parsed.getUTCMonth() + 1).padStart(2, "0"),
    String(parsed.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

// Datasource provenance identity for a SINGLE operation/round: country +
// operation + round_number + reporting_date. This is deliberately per-round
// (not per-row) so that two rows citing the same round in a different
// array order still resolve to the same identity, and so a row backed by
// multiple rounds can be annotated with multiple, independently-resolved
// datasource numbers. Every field is normalized before joining so that this
// key is the single canonical registry lookup for the entire compact
// summary response — identical real-world provenance cited from different
// sections/support-row families (Overview, Reason, Admin1, Key Figures)
// always collapses to the same key, even if those sections' rows arrived
// via different backend generation paths with slightly different text
// formatting for what is otherwise the same round.
const getSummarySupportRoundKey = (round, fallbackCountry) => {
  const country = normalizeProvenanceText(
    resolveSummarySupportRoundCountry(round, fallbackCountry),
  );
  const operation = normalizeProvenanceText(round?.operation || "").toLowerCase();
  const roundNumber = normalizeProvenanceText(round?.round_number ?? round?.round ?? "");
  const date = normalizeProvenanceDate(getSupportRoundDate(round) || "");

  if (!country && !operation && !roundNumber && !date) return null;

  return [country.toLowerCase(), operation, roundNumber, date].join("|");
};

// The unique canonical provenance sources referenced by a support row, deduped
// within the row itself (a row that cites the same round twice must not be
// treated as two sources) and with the resolved country baked onto each round
// so downstream rendering never falls back to "Unknown country" needlessly.
const getSummarySupportRoundEntries = (row, fallbackCountry) => {
  const rounds = normalizeSupportOperationRounds(row?.source_operation_rounds);
  const seenKeys = new Set();
  const entries = [];

  rounds.forEach((round) => {
    const key = getSummarySupportRoundKey(round, fallbackCountry);
    if (!key || seenKeys.has(key)) return;

    seenKeys.add(key);
    entries.push({
      key,
      round: {
        ...round,
        country: resolveSummarySupportRoundCountry(round, fallbackCountry),
      },
    });
  });

  return entries;
};

const createSummaryAnnotator = (rows, summarySupportTables = null, fallbackCountry = "") => {
  const rowById = new Map(
    (Array.isArray(rows) ? rows : [])
      .flatMap((row, index) =>
        getRowIdentifierValues(row, index).map((rowId) => [rowId, row]),
      ),
  );
  const sourceByKey = new Map();
  const orderByProvenanceKey = new Map();
  const sources = [];
  const parser = new DOMParser();
  let nextOrder = 1;

  const allocateOrder = (provenanceKey) => {
    if (!provenanceKey) return nextOrder++;

    if (!orderByProvenanceKey.has(provenanceKey)) {
      orderByProvenanceKey.set(provenanceKey, nextOrder++);
    }

    return orderByProvenanceKey.get(provenanceKey);
  };

  // Resolves every unique provenance source a support row is backed by,
  // each to its own (shared, first-occurrence) datasource number. A
  // calculated/derived row (e.g. an average) gets no numbers at all: it's
  // not an observed provenance source, so it neither mints nor reuses a
  // datasource entry.
  const resolveSummarySupportOrders = (row) => {
    if (isCalculatedSummarySupportRow(row)) {
      return { orders: [], provenanceRounds: [] };
    }

    const roundEntries = getSummarySupportRoundEntries(row, fallbackCountry);
    if (!roundEntries.length) {
      return { orders: [allocateOrder(null)], provenanceRounds: [] };
    }

    const seenOrders = new Set();
    const provenanceRounds = [];

    roundEntries.forEach((entry) => {
      const order = allocateOrder(entry.key);
      if (seenOrders.has(order)) return;

      seenOrders.add(order);
      provenanceRounds.push({ order, round: entry.round });
    });

    provenanceRounds.sort((left, right) => left.order - right.order);

    return {
      orders: provenanceRounds.map((entry) => entry.order),
      provenanceRounds,
    };
  };

  const getOrCreateSource = ({
    key,
    rowId,
    row,
    type,
    section,
    sectionTitle,
    orders = [],
    provenanceRounds = [],
  }) => {
    if (!sourceByKey.has(key)) {
      const source = {
        order: orders.length > 0 ? orders[0] : null,
        orders,
        provenanceRounds,
        rowId: String(rowId),
        row,
        type,
        section,
        sectionTitle,
      };
      sourceByKey.set(key, source);
      sources.push(source);
    }

    return sourceByKey.get(key);
  };

  const markClickableCitation = (node, title = "Open data actions") => {
    node.classList.add(classes.citation_figure);
    node.setAttribute("role", "button");
    node.setAttribute("tabindex", "0");
    node.setAttribute("title", title);
  };

  const annotateHtml = (html) => {
    const sanitizedHtml = sanitizeHtml(html);
    const doc = parser.parseFromString(sanitizedHtml, "text/html");

    doc.body.querySelectorAll(
      [
        "[data-data-row-id]",
        "[data-row-id]",
        "[data-source-row-id]",
        "[data-query-row-id]",
        "[data-rank]",
        "[data-summary-row-id]",
      ].join(","),
    )
      .forEach((node) => {
        const summaryRowId = node.getAttribute("data-summary-row-id");
        let source = null;

        if (summaryRowId) {
          const resolved = resolveSummarySupportRow(summarySupportTables, summaryRowId);

          if (!resolved) {
            markClickableCitation(node, "Source details unavailable");
            return;
          }

          // Every support row stays clickable (including calculated/derived
          // rows like an average) so its existing support table still
          // opens — only the numbered datasource annotation is conditional.
          markClickableCitation(node, "Open source details");

          const { orders, provenanceRounds } = resolveSummarySupportOrders(resolved.row);

          source = getOrCreateSource({
            key: `summary:${summaryRowId}`,
            rowId: summaryRowId,
            row: resolved.row,
            type: "summary_support",
            section: resolved.section,
            sectionTitle: resolved.sectionTitle,
            orders,
            provenanceRounds,
          });
        } else {
          const rowId = getCitationRowId(node);
          const row = rowById.get(String(rowId));

          if (!row) return;

          source = getOrCreateSource({
            key: `query:${rowId}`,
            rowId,
            row,
            type: "query_result",
            orders: [allocateOrder(getQueryResultProvenanceKey(row))],
          });

          markClickableCitation(node);
          if (!node.getAttribute("data-data-row-id")) {
            node.setAttribute("data-data-row-id", String(rowId));
          }
        }

        // A row backed by multiple distinct datasource numbers (e.g. two
        // operation/round sources) renders as ONE superscript with a
        // comma-separated list ("1,2"), not one sup per number and not a
        // plain-text comma between separate sups.
        if (source.orders && source.orders.length > 0) {
          const sup = doc.createElement("sup");
          sup.className = classes.citation_sup;
          sup.setAttribute("contenteditable", "false");
          const orderLabel = source.orders.join(",");
          sup.setAttribute("data-citation-order", orderLabel);
          sup.textContent = orderLabel;
          node.appendChild(sup);
        }
      });

    return doc.body.innerHTML;
  };

  return {
    annotateHtml,
    sources,
  };
};

export const annotateSummary = (html, rows, summarySupportTables = null, fallbackCountry = "") => {
  const annotator = createSummaryAnnotator(rows, summarySupportTables, fallbackCountry);

  return {
    html: annotator.annotateHtml(html),
    sources: annotator.sources,
  };
};

const annotateSummarySections = (sections, rows, summarySupportTables = null, fallbackCountry = "") => {
  if (!Array.isArray(sections) || sections.length === 0) {
    return { sections: [], sources: [] };
  }

  const annotator = createSummaryAnnotator(rows, summarySupportTables, fallbackCountry);

  return {
    sections: sections.map((section) => ({
      ...section,
      paragraphs: Array.isArray(section?.paragraphs)
        ? section.paragraphs.map((paragraph) =>
            annotator.annotateHtml(paragraph),
          )
        : [],
    })),
    sources: annotator.sources,
  };
};
const formatCellValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
const formatSupportDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") return value.toLocaleString("en-US");
  if (typeof value === "object") return JSON.stringify(value);

  const number = Number(value);
  return String(value).trim() !== "" && Number.isFinite(number)
    ? number.toLocaleString("en-US")
    : String(value);
};

const normalizeSupportOperationRounds = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) return parsedValue;
  if (!parsedValue) return [];
  if (Array.isArray(parsedValue.rows)) return parsedValue.rows;
  if (Array.isArray(parsedValue.rounds)) return parsedValue.rounds;
  if (Array.isArray(parsedValue.data)) return parsedValue.data;

  return [parsedValue];
};

const getSupportRoundDate = (round) => {
  if (round?.reporting_date_range) return round.reporting_date_range;
  if (Array.isArray(round?.reporting_dates)) {
    return round.reporting_dates.filter(Boolean).join(", ");
  }
  return round?.reporting_dates || round?.reporting_date || round?.date || "";
};

const getSupportEntityName = (row) =>
  row?.dimension_value ||
  row?.label ||
  row?.dimension_label ||
  row?.name ||
  row?.section ||
  "Source";

const SUMMARY_SUPPORT_POPULATION_TYPES = new Set(["total", "male", "female"]);

// Row-level population/metric context (total | male | female) for a summary
// support row, resolved from the row's own structured identity fields —
// never from the rendered summary sentence/prompt text, which describes the
// whole response and may span several rows with different metrics. "female"
// is checked before "male" since it contains that substring.
const getSummarySupportRowPopulationType = (row, section) => {
  const explicit = String(
    row?.population_type ??
      row?.populationType ??
      row?.metric ??
      row?.dimension_type ??
      "",
  ).toLowerCase();
  if (SUMMARY_SUPPORT_POPULATION_TYPES.has(explicit)) return explicit;

  // Admin1/reason rows carry a place/cause name in this same identity, not a
  // population metric, so they always default to "total" rather than being
  // scanned for "male"/"female" substrings.
  if (section === "location" || section === "reason") return "total";

  const identity = normalizeSupportText(
    [row?.dimension_value, row?.label, row?.dimension_label, row?.name, row?.id]
      .filter(Boolean)
      .join(" "),
  );
  if (/\bfemale\b/.test(identity)) return "female";
  if (/\bmale\b/.test(identity)) return "male";
  return "total";
};

// Row-level Admin1 geography context, resolved from the row's own fields
// using the same admin_1_name/admin_1_pcode field-name convention already
// used for row-level Admin1 identity elsewhere in this app (see Map.js,
// IDPMovement.js). Only rows belonging to the "location" (Admin1) support
// section carry this context — every other row resolves to null so a click
// on a non-Admin1 datapoint never keeps a previously-selected Admin1 active.
const getSummarySupportRowAdmin1 = (row, section) => {
  if (section !== "location") return null;

  const name = String(
    row?.admin1 ||
      row?.admin_1_name ||
      row?.admin1_name ||
      row?.admin_1_name_en ||
      row?.dimension_value ||
      row?.label ||
      "",
  ).trim();
  if (!name) return null;

  const topLevelPcode = String(
    row?.admin1pcode || row?.admin_1_pcode || row?.admin1_pcode || "",
  ).trim();
  if (topLevelPcode) return { name, pcode: topLevelPcode };

  // The live backend "location" support row carries no top-level pcode —
  // it only lives on each contributing source_operation_rounds entry
  // (admin_1 / admin1_pcode), since one Admin1 row can aggregate several
  // operations/rounds for the same place. Match rounds back to this row's
  // resolved name (case-insensitive, trimmed) rather than assuming index 0.
  const matchingPcodes = new Set(
    normalizeSupportOperationRounds(row?.source_operation_rounds)
      .filter(
        (round) =>
          String(round?.admin_1 || "").trim().toLowerCase() === name.toLowerCase(),
      )
      .map((round) => String(round?.admin1_pcode || "").trim())
      .filter(Boolean),
  );

  // Zero matches -> no pcode available. More than one distinct value ->
  // conflicting source data; do not guess, leave pcode empty.
  const pcode = matchingPcodes.size === 1 ? [...matchingPcodes][0] : "";

  return { name, pcode };
};

// Row-level reason/cause context. Only rows belonging to the "reason"
// support section resolve a cause here; every other row keeps the existing
// round-level reason_for_displacement fallback in openRound below, so this
// never overrides that already-working behavior for non-reason rows.
const getSummarySupportRowCause = (row, section) => {
  if (section !== "reason") return null;

  return (
    String(
      row?.reason ||
        row?.cause ||
        row?.reason_for_displacement ||
        row?.dimension_value ||
        row?.label ||
        "",
    ).trim() || null
  );
};

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getSupportFigureValue = (row, entityName) => {
  const displayValue = row?.display_value || formatSupportDisplayValue(row?.value);
  if (!displayValue) return "";

  const entity = String(entityName || "").trim();
  if (!entity) return displayValue;

  const withoutLeadingEntity = String(displayValue)
    .replace(new RegExp(`^\\s*${escapeRegExp(entity)}\\s*(?:with\\s*)?`, "i"), "")
    .replace(/^[-:��|,]+\s*/, "")
    .trim();

  return withoutLeadingEntity || displayValue;
};
const getSupportFreshnessMeta = (rounds) => {
  for (const round of rounds) {
    const label =
      round?.round_freshness_label ||
      round?.freshness_label ||
      round?.freshnessLabel;
    if (label) {
      return {
        label,
        color: String(round?.round_freshness_color || round?.freshness_color || "gray"),
        description: label,
      };
    }

    const rawFreshness =
      round?.round_freshness ??
      round?.freshness ??
      round?.fr ??
      round?.f;
    if (rawFreshness !== null && rawFreshness !== undefined && rawFreshness !== "") {
      const freshness = getDataFreshness(rawFreshness);
      return freshness || {
        label: String(rawFreshness),
        color: "gray",
        description: String(rawFreshness),
      };
    }
  }

  return null;
};

const SummarySupportOpenIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <path
      d="M7 17 17 7M9 7h8v8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const countryPcodeMap = (name) => {
  const all = [   {"admin_0_pcode":"ZWE","admin_0_name":"Zimbabwe"},   {"admin_0_pcode":"ZMB","admin_0_name":"Zambia"},   {"admin_0_pcode":"YEM","admin_0_name":"Yemen"},   {"admin_0_pcode":"VUT","admin_0_name":"Vanuatu"},   {"admin_0_pcode":"UKR","admin_0_name":"Ukraine"},   {"admin_0_pcode":"UGA","admin_0_name":"Uganda"},   {"admin_0_pcode":"SYR","admin_0_name":"Syrian Arab Republic"},   {"admin_0_pcode":"SDN","admin_0_name":"Sudan"},   {"admin_0_pcode":"LKA","admin_0_name":"Sri lanka"},   {"admin_0_pcode":"SSD","admin_0_name":"South Sudan"},   {"admin_0_pcode":"ZAF","admin_0_name":"South Africa"},   {"admin_0_pcode":"SOM","admin_0_name":"Somalia"},   {"admin_0_pcode":"VCT","admin_0_name":"Saint Vincent and the Grenadines"},   {"admin_0_pcode":"ARM","admin_0_name":"Republic of Armenia"},   {"admin_0_pcode":"PHL","admin_0_name":"Philippines (the)"},   {"admin_0_pcode":"PER","admin_0_name":"Peru"},   {"admin_0_pcode":"PNG","admin_0_name":"Papua New Guinea"},   {"admin_0_pcode":"PAK","admin_0_name":"Pakistan"},   {"admin_0_pcode":"NGA","admin_0_name":"Nigeria"},   {"admin_0_pcode":"NER","admin_0_name":"Niger"},   {"admin_0_pcode":"NPL","admin_0_name":"Nepal"},   {"admin_0_pcode":"MOZ","admin_0_name":"Mozambique"},   {"admin_0_pcode":"MNG","admin_0_name":"Mongolia"},   {"admin_0_pcode":"MLI","admin_0_name":"Mali"},   {"admin_0_pcode":"MWI","admin_0_name":"Malawi"},   {"admin_0_pcode":"MDG","admin_0_name":"Madagascar"},   {"admin_0_pcode":"LBY","admin_0_name":"Libya"},   {"admin_0_pcode":"LBN","admin_0_name":"Lebanon"},   {"admin_0_pcode":"LAO","admin_0_name":"Lao People's Democratic Republic"},   {"admin_0_pcode":"KEN","admin_0_name":"Kenya"},   {"admin_0_pcode":"IRQ","admin_0_name":"Iraq"},   {"admin_0_pcode":"IDN","admin_0_name":"Indonesia"},   {"admin_0_pcode":"HND","admin_0_name":"Honduras"},   {"admin_0_pcode":"HTI","admin_0_name":"Haiti"},   {"admin_0_pcode":"GTM","admin_0_name":"Guatemala"},   {"admin_0_pcode":"GRD","admin_0_name":"Grenada"},   {"admin_0_pcode":"FJI","admin_0_name":"Fiji"},   {"admin_0_pcode":"ETH","admin_0_name":"Ethiopia"},   {"admin_0_pcode":"SLV","admin_0_name":"El Salvador"},   {"admin_0_pcode":"ECU","admin_0_name":"Ecuador"},   {"admin_0_pcode":"DMA","admin_0_name":"Dominica"},   {"admin_0_pcode":"DJI","admin_0_name":"Djibouti"},   {"admin_0_pcode":"COD","admin_0_name":"Democratic Republic of the Congo"},   {"admin_0_pcode":"TCD","admin_0_name":"Chad"},   {"admin_0_pcode":"CAF","admin_0_name":"Central African Republic"},   {"admin_0_pcode":"CMR","admin_0_name":"Cameroon"},   {"admin_0_pcode":"BDI","admin_0_name":"Burundi"},   {"admin_0_pcode":"BFA","admin_0_name":"Burkina Faso"},   {"admin_0_pcode":"BOL","admin_0_name":"Bolivia (Plurinational State of)"},   {"admin_0_pcode":"BEN","admin_0_name":"Benin"},   {"admin_0_pcode":"BGD","admin_0_name":"Bangladesh"},   {"admin_0_pcode":"BHS","admin_0_name":"Bahamas (the)"},   {"admin_0_pcode":"ATG","admin_0_name":"Antigua and Barbuda"},   {"admin_0_pcode":"AFG","admin_0_name":"Afghanistan"} ]
  const country = all.filter((c) => c.admin_0_name == name)
  if(country.length == 0 ) return 0;
  console.log(name, country[0].admin_0_pcode)
  return country[0].admin_0_pcode
}
const getDistinctSupportCountries = (rounds) =>
  Array.from(
    new Set(
      rounds
        .map((round) => String(round?.country || "").trim())
        .filter(Boolean),
    ),
  );

const SummarySupportOperationRounds = ({
  onClose,
  onRoundClick,
  rounds,
  row,
  section,
  summaryRowId,
}) => {
    const dispatch = useDispatch();
    const selectedCountry = useSelector((state) => state.selection.country);
    const selectedCountryPcode = useSelector((state) => state.selection.countrypcode);
    const selectedAdmin1 = useSelector((state) => state.selection.admin1);
    const selectedAdmin2 = useSelector((state) => state.selection.admin2);
    const selectedYear = useSelector((state) => state.selection.year);
    const selectedCause = useSelector((state) => state.selection.cause);
    const selectedOperation = useSelector((state) => state.selection.operation);
  if (!rounds.length) return null;

  const showCountry = getDistinctSupportCountries(rounds).length > 1;
  // Reuse the exact same working "source record -> load on map" behavior as
  // the Data Sources panel, instead of keeping a separate copy of the
  // selection dispatches here.
  const openRound = (round) => {
    const targetCountry = round?.country || "";
    // Match handleVisualizeDataClick's pcode resolution: the shared,
    // normalized ISO3 lookup, not a hardcoded country->ISO map.
    const targetPcode = getIso3ByCountryName(targetCountry) || "";
    const targetOperation = round?.operation || "All";
    const targetRound = round?.round_number ?? round?.round ?? null;
    // The table's own Year column reads round?.year directly, confirming
    // this is the field actually populated on these round objects.
    const rowYear = Number(round?.year);
    // Structured, row-level context: which of the clicked support row's own
    // dimensions (metric/admin1/reason) are genuinely part of it, resolved
    // from the row's structured fields rather than rendered text. Rows that
    // don't carry a given dimension resolve to null/"total" so a click never
    // inherits stale context from whatever was previously selected.
    const targetPopulationType = getSummarySupportRowPopulationType(row, section);
    const targetAdmin1 = getSummarySupportRowAdmin1(row, section);
    const rowCause =
      getSummarySupportRowCause(row, section) ||
      round?.reason_for_displacement ||
      "All";

    // Copied directly from the working handleVisualizeDataClick dispatch
    // sequence (AIResponseSource.js) rather than sharing a helper.
    const mapWillReload =
      selectedCountry !== targetCountry ||
      selectedCountryPcode !== targetPcode ||
      selectedAdmin1 !== (targetAdmin1?.name || "") ||
      selectedAdmin2 !== "" ||
      (Number.isFinite(rowYear) && Number(selectedYear) !== rowYear) ||
      selectedCause !== rowCause ||
      selectedOperation !== targetOperation;

    if (mapWillReload) {
      dispatch(selectionsSliceActions.setMapLoading(true));
    }
    dispatch(selectionsSliceActions.setCountry(targetCountry));
    dispatch(selectionsSliceActions.setCountryPcode(targetPcode));
    dispatch(selectionsSliceActions.setAdmin1(targetAdmin1?.name || ""));
    dispatch(selectionsSliceActions.setAdmin1Pcode(targetAdmin1?.pcode || ""));
    dispatch(selectionsSliceActions.setAdmin2(""));
    if (Number.isFinite(rowYear)) {
      dispatch(selectionsSliceActions.setYear(rowYear));
    }
    dispatch(selectionsSliceActions.setCause(rowCause));
    dispatch(selectionsSliceActions.setOperation(targetOperation));
    dispatch(selectionsSliceActions.setRound(targetRound));
    dispatch(selectionsSliceActions.setRoundFilter(targetRound));
    dispatch(selectionsSliceActions.setPopulationType(targetPopulationType));
    onClose?.();
  }
  return (
    <div className={classes.summary_support_rounds}>
      <table className={classes.summary_support_rounds_table}>
        <thead>
          <tr>
            {showCountry && <th>Country</th>}
            <th>Operation</th>
            <th>Round</th>
            <th>Date</th>
            <th>Year</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map((round, index) => {
            const operation = round?.operation || "";
            return (
              <tr
                key={round?.id || `${operation || "round"}-${index}`}
                className={classes.summary_support_round_row}
                role="button"
                tabIndex={0}
                /*onClick={() => onRoundClick?.(summaryRowId, round)}*/
                onClick={() => openRound(round)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openRound(round);
                  }
                }}
              >
                {showCountry && <td>{round?.country || ""}</td>}
                <td>
                  <span className={classes.summary_support_operation} title={operation}>
                    {operation}
                  </span>
                </td>
                <td>{round?.round_number ?? round?.round ?? ""}</td>
                <td>{formatSupportRoundDate(round)}</td>
                <td>{round?.year || ""}</td>
                <td>{formatSupportDisplayValue(round?.value)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SummarySupportDetails = ({ onClose, onOpen, onRoundClick, source }) => {
  if (!source?.row) {
    return (
      <div className={classes.summary_support_details}>
        Source details unavailable
      </div>
    );
  }

  const row = source.row;
  const rounds = normalizeSupportOperationRounds(row.source_operation_rounds);
  const entityName = getSupportEntityName(row);
  const displayValue = getSupportFigureValue(row, entityName);
  const firstRound = rounds[0] || null;
  const freshness = getSupportFreshnessMeta(rounds);

  return (
    <div className={classes.summary_support_details}>
      <div className={classes.summary_support_header}>
        <div className={classes.summary_support_heading_main}>
          <span className={classes.summary_support_entity} title={entityName}>
            {entityName}
          </span>
          {displayValue && (
            <span className={classes.summary_support_value}>{displayValue}</span>
          )}
          {/*
          <button
            type="button"
            className={classes.summary_support_open_button}
            aria-label="Visualize related source data"
            title="Visualize related source data"
            disabled={!firstRound}
            onClick={() => {
              if (!firstRound) return;
              onOpen?.(source.rowId, firstRound);
            }}
          >
            <span>Visualize</span>
            <SummarySupportOpenIcon />
          </button>*/}
        </div>
        {freshness && false && (
          <div className={classes.summary_support_badges}>
            <span
              className={`${classes.summary_support_badge} ${
                classes[`summary_support_badge_${freshness.color}`] || ""
              }`}
              title={freshness.description || freshness.label}
            >
              {freshness.label}
            </span>
          </div>
          
        )}
      </div>
      <SummarySupportOperationRounds
        rounds={rounds}
        row={row}
        section={source.section}
        summaryRowId={source.rowId}
        onRoundClick={onRoundClick}
        onClose={onClose}
      />
    </div>
  );
};

const isFreshnessColumn = (column) =>
  ["freshness", "round_freshness"].includes(
    String(column || "").toLowerCase(),
  );

const normalizeColumnName = (column, label = "") =>
  `${column} ${label}`.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const getIdpCountColumnType = (column, label = "") => {
  const normalized = normalizeColumnName(column, label);

  if (/\bfemale\b/.test(normalized) && /\bidps?\b/.test(normalized)) {
    return "female";
  }

  if (/\bmale\b/.test(normalized) && /\bidps?\b/.test(normalized)) {
    return "male";
  }

  if (/\btotal\b/.test(normalized) && /\bidps?\b/.test(normalized)) {
    return "total";
  }

  return "";
};

const formatIdpCountValue = (value, columnType) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (
    (columnType === "male" || columnType === "female") &&
    Number(value) === 0
  ) {
    return "";
  }

  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-US")
    : formatCellValue(value);
};

const isReportingDateColumn = (column, label = "") => {
  const normalized = normalizeColumnName(column, label);
  return /\breporting date\b/.test(normalized);
};

const formatReportingDate = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatCellValue(value);

  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

const FreshnessBadge = ({ value }) => {
  const freshness = getDataFreshness(value);

  if (!freshness) return formatCellValue(value);

  return (
    <span
      className={`${classes.freshness_badge} ${
        classes[`freshness_badge_${freshness.color}`]
      }`}
      title={freshness.description}
    >
      {freshness.label}
    </span>
  );
};

const renderTableCell = (column, value, label = "") => {
  if (isFreshnessColumn(column)) {
    return <FreshnessBadge value={value} />;
  }

  if (isReportingDateColumn(column, label)) {
    return formatReportingDate(value);
  }

  const idpCountColumnType = getIdpCountColumnType(column, label);
  return idpCountColumnType
    ? formatIdpCountValue(value, idpCountColumnType)
    : formatCellValue(value);
};

const renderOperationBreakdownCell = (column, value) => {
  if (column === "freshness") {
    return <FreshnessBadge value={value} />;
  }

  if (column === "reporting date") {
    return formatReportingDate(value);
  }

  if (column === "Total idp") {
    return formatIdpCountValue(value, "total");
  }

  if (column === "female idps") {
    return formatIdpCountValue(value, "female");
  }

  if (column === "male idps") {
    return formatIdpCountValue(value, "male");
  }

  return formatCellValue(value);
};

const getSummaryTable = (response) =>
  response?.summary?.summary_table ||
  response?.summary?.table ||
  response?.summary_table ||
  null;

const getSummaryTableColumnKey = (column) =>
  typeof column === "string" ? column : column?.key ?? column?.field ?? "";

const getSummaryTableColumnLabel = (column) =>
  typeof column === "string"
    ? column
    : column?.label || column?.title || column?.key || column?.field || "";

const getSummaryTableCellValue = (row, column, columnIndex) => {
  if (Array.isArray(row)) return row[columnIndex];
  const columnKey = getSummaryTableColumnKey(column);
  return columnKey ? row?.[columnKey] : "";
};

const SummaryTable = ({ table }) => {
  const columns = Array.isArray(table?.columns) ? table.columns : [];
  const rows = Array.isArray(table?.rows) ? table.rows : [];

  if (!columns.length || !rows.length) return null;

  return (
    <div className={classes.summary_table_wrap}>
      <table className={classes.summary_table}>
        <thead>
          <tr>
            {columns.map((column, columnIndex) => (
              <th key={getSummaryTableColumnKey(column) || columnIndex}>
                {getSummaryTableColumnLabel(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={row?.id || row?.RowId || rowIndex}>
              {columns.map((column, columnIndex) => (
                <td key={getSummaryTableColumnKey(column) || columnIndex}>
                  {formatCellValue(
                    getSummaryTableCellValue(row, column, columnIndex),
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AMBIGUOUS_ROUND_SNAPSHOT_HEADERS = [
  "operation",
  "round",
  "reporting date",
  "total idps",
];

const normalizeSummaryTableHeader = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const isAmbiguousRoundSnapshotTable = (table) => {
  const headerRow = table?.tHead?.rows?.[0];
  const headers = Array.from(headerRow?.cells || []).map((cell) =>
    normalizeSummaryTableHeader(cell.textContent),
  );

  return (
    headers.length === AMBIGUOUS_ROUND_SNAPSHOT_HEADERS.length &&
    headers.every(
      (header, index) => header === AMBIGUOUS_ROUND_SNAPSHOT_HEADERS[index],
    )
  );
};

const getReportingYear = (value) => {
  const matches = String(value || "").match(/\b(?:19|20)\d{2}\b/g);
  return matches?.[matches.length - 1] || "";
};

const REPORTING_MONTHS = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

export const parseRoundAnchorReportingDate = (value) => {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  const match = normalized.match(/^(\d{1,2})\s+([a-z]+)\s+(\d{4})$/i);
  if (!match) return "";

  const day = Number(match[1]);
  const month = REPORTING_MONTHS[match[2].toLowerCase()];
  const year = Number(match[3]);
  if (!month) return "";

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const TRAILING_OPERATION_REFINEMENT =
  /\s+for operation\s+.+?(?:\s+in(?:\s+the\s+year)?\s+\d{4})?\s*$/i;

export const buildRoundSnapshotOptionPrompt = (
  originalPrompt,
  operation,
  year,
) => {
  const basePrompt = String(originalPrompt || "")
    .replace(TRAILING_OPERATION_REFINEMENT, "")
    .trim();
  const operationName = String(operation || "").trim();
  const reportingYear = String(year || "").trim();

  if (!basePrompt || !operationName) return basePrompt;

  return `${basePrompt} for operation ${operationName}${
    reportingYear ? ` in the year ${reportingYear}` : ""
  }`;
};

const prepareAmbiguousRoundSnapshotSummary = (html) => {
  if (!html || typeof DOMParser === "undefined") {
    return { html: html || "", operationOptions: [] };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const operationOptions = [];

  doc.body.querySelectorAll("table").forEach((table) => {
    if (!isAmbiguousRoundSnapshotTable(table)) return;

    table.classList.add(overviewClasses.table);
    table.setAttribute("aria-label", "Round snapshot operation options");

    const wrapper = doc.createElement("div");
    wrapper.className = overviewClasses.tableScroll;
    wrapper.setAttribute("role", "region");
    wrapper.setAttribute("aria-label", "Round snapshot operation options table");
    wrapper.setAttribute("tabindex", "0");
    table.replaceWith(wrapper);
    wrapper.appendChild(table);

    Array.from(table.tBodies || []).forEach((body) => {
      Array.from(body.rows || []).forEach((row) => {
        const operationCell = row.cells?.[0];
        const operation = operationCell?.textContent?.trim();
        if (!operationCell || !operation) return;

        const reportingDate = row.cells?.[2]?.textContent;
        const year = getReportingYear(reportingDate);
        const roundAnchorReportingDate =
          parseRoundAnchorReportingDate(reportingDate);
        const optionIndex = operationOptions.push({
          operation,
          roundAnchorReportingDate,
          year,
        }) - 1;
        const mount = doc.createElement("span");
        mount.setAttribute(
          "data-ambiguous-round-operation-option",
          String(optionIndex),
        );
        operationCell.replaceChildren(mount);
      });
    });
  });

  return { html: doc.body.innerHTML, operationOptions };
};

const AmbiguousRoundSnapshotSummary = ({ html, onOperationClick }) => {
  const summaryRef = useRef(null);
  const [operationTargets, setOperationTargets] = useState([]);
  const parsedSummary = useMemo(
    () => prepareAmbiguousRoundSnapshotSummary(html),
    [html],
  );

  useEffect(() => {
    const targets = Array.from(
      summaryRef.current?.querySelectorAll(
        "[data-ambiguous-round-operation-option]",
      ) || [],
    ).map((node) => ({
      node,
      option:
        parsedSummary.operationOptions[
          Number(node.getAttribute("data-ambiguous-round-operation-option"))
        ],
    }));

    setOperationTargets(targets.filter((target) => target.option?.operation));
  }, [parsedSummary]);

  return (
    <>
      <div
        ref={summaryRef}
        className={classes.summary}
        dangerouslySetInnerHTML={{ __html: parsedSummary.html }}
      />
      {operationTargets.map(({ node, option }, index) =>
        createPortal(
          <button
            type="button"
            className={classes.summary_operation_option}
            onClick={() => onOperationClick?.(option)}
          >
            {option.operation}
          </button>,
          node,
          `${option.operation}-${option.roundAnchorReportingDate}-${index}`,
        ),
      )}
    </>
  );
};

const formatClarificationRound = (round) => {
  if (round === null || round === undefined || round === "") return "";
  const value =
    typeof round === "object"
      ? round.round_number ?? round.round ?? round.label
      : round;
  if (value === null || value === undefined || value === "") return "";
  const label = String(value);
  return /^R\d+/i.test(label) ? label : `R${label}`;
};

const formatClarificationRounds = (rounds, separator = "; ") => {
  if (rounds === null || rounds === undefined || rounds === "") return "";
  const values = Array.isArray(rounds) ? rounds : [rounds];

  return values
    .map((round) => {
      const label = formatClarificationRound(round);
      if (!label) return "";
      const reportingDate =
        typeof round === "object"
          ? round.reporting_date ?? round.reportingDate ?? round.date
          : "";
      return reportingDate ? `${label} \u2014 ${reportingDate}` : label;
    })
    .filter(Boolean)
    .join(separator);
};

const getClarificationRoundBadges = (rounds) => {
  const values = Array.isArray(rounds)
    ? rounds
    : rounds === null || rounds === undefined || rounds === ""
      ? []
      : [rounds];

  return values
    .map((round) => {
      const label = formatClarificationRound(round);
      if (!label) return "";
      const reportingDate =
        typeof round === "object"
          ? round.reporting_date ?? round.reportingDate ?? round.date
          : "";
      return reportingDate ? `${label} \u2014 ${reportingDate}` : label;
    })
    .filter(Boolean);
};

const getClarificationActionLabel = (option, clarificationType = "") => {
  const providedActionLabel = String(option?.action_label || "").trim();
  if (providedActionLabel) return providedActionLabel;

  const actionQuery = String(option?.action_query || "")
    .trim()
    .toLowerCase();
  const actionType = String(option?.action_type || "").toLowerCase();
  const isChangeFromPrevious =
    actionType.includes("change") ||
    (clarificationType === "multiple_reporting_dates" &&
      /(what changed|show changes?|how did|change in)/i.test(actionQuery));

  if (isChangeFromPrevious) return "Analyze change";
  if (actionQuery.startsWith("compare")) return "Compare";
  if (
    actionQuery.startsWith("analyze") ||
    actionQuery.startsWith("show the trend")
  ) {
    return "Analyze";
  }
  if (actionQuery.includes("snapshot")) return "Show snapshot";
  return "Open";
};
const getClarificationSuggestions = (clarification) => {
  const suggestions = clarification?.suggestions;
  return Array.isArray(suggestions) ? suggestions : [];
};

const ExplicitRoundClarificationCard = ({ clarification, onActionQuery }) => {
  if (!clarification || typeof clarification !== "object") return null;

  const clarificationType = String(
    clarification.type || clarification.clarification_type || "",
  ).toLowerCase();
  const message =
    clarification.message || clarification.summary_text || clarification.text || "";
  const operationOptions = [
    clarification.operation_options,
    clarification.options,
    clarification.operations,
  ].find(Array.isArray) || [];
  const suggestions = getClarificationSuggestions(clarification);
  const roundDetails = [
    ["Requested", clarification.requested_rounds],
    ["Found", clarification.found_rounds],
    ["Missing", clarification.missing_rounds],
    ["Available", clarification.available_rounds],
  ].filter(([, rounds]) =>
    Array.isArray(rounds)
      ? rounds.length > 0
      : rounds !== undefined && rounds !== null && rounds !== "",
  );

  const submitAction = (actionQuery, explicitRoundAnchorContext) => {
    const query = String(actionQuery || "").trim();
    if (query) onActionQuery?.(query, explicitRoundAnchorContext);
  };

  return (
    <section
      className={classes.explicit_round_clarification_card}
      aria-label="Explicit round clarification"
    >
      {message && (
        <div className={classes.clarification_message} role="status">
          <span
            className={`${classes.status_icon} ${classes.status_icon_info}`}
            aria-hidden="true"
          >
            i
          </span>
          <div className={classes.clarification_message_body}>{message}</div>
        </div>
      )}

      {operationOptions.length > 0 && (
        <div
          className={classes.explicit_round_option_list}
          role="list"
          aria-label="Explicit round operation options"
        >
          {operationOptions.map((option, index) => {
            const operation =
              option?.operation || option?.name || option?.label || "Selected rounds";
            const rounds =
              option?.rounds ??
              option?.selected_rounds ??
              option?.round_details ??
              (option?.round_number || option?.reporting_date ? [option] : []);
            const roundBadges = getClarificationRoundBadges(rounds);
            const actionQuery = option?.action_query;

            return (
              <div
                key={`${operation}-${index}`}
                className={classes.explicit_round_option}
                role="listitem"
              >
                <div className={classes.explicit_round_operation_name}>
                  {operation}
                </div>
                {roundBadges.length > 0 && (
                  <div
                    className={classes.explicit_round_badges}
                    aria-label={`Rounds for ${operation}`}
                  >
                    {roundBadges.map((roundBadge, badgeIndex) => (
                      <span
                        key={`${roundBadge}-${badgeIndex}`}
                        className={classes.explicit_round_badge}
                      >
                        {roundBadge}
                      </span>
                    ))}
                  </div>
                )}
                {actionQuery && (
                  <button
                    type="button"
                    className={classes.explicit_round_small_action_button}
                    onClick={() =>
                      submitAction(
                        actionQuery,
                        option?.explicit_round_anchor_context,
                      )
                    }
                  >
                    {getClarificationActionLabel(option, clarificationType)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {clarificationType === "missing_rounds" && roundDetails.length > 0 && (
        <dl>
          {roundDetails.map(([label, rounds]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{formatClarificationRounds(rounds, ", ")}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      {suggestions.length > 0 && (
        <div className={classes.data_gap_suggestions}>
          <div className={classes.data_gap_suggestions_label}>Try:</div>
          <div className={classes.data_gap_suggestion_list}>
            {suggestions.map((suggestion, index) => {
              const actionQuery = suggestion?.action_query;
              if (!actionQuery) return null;

              return (
                <button
                  key={suggestion?.id || `${actionQuery}-${index}`}
                  type="button"
                  className={classes.explicit_round_small_action_button}
                  onClick={() =>
                    submitAction(
                      actionQuery,
                      suggestion?.explicit_round_anchor_context,
                    )
                  }
                >
                  {getClarificationActionLabel(suggestion, clarificationType)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};
const compareTableValues = (leftValue, rightValue) => {
  if (leftValue === null || leftValue === undefined) return 1;
  if (rightValue === null || rightValue === undefined) return -1;

  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);

  if (
    leftValue !== "" &&
    rightValue !== "" &&
    Number.isFinite(leftNumber) &&
    Number.isFinite(rightNumber)
  ) {
    return leftNumber - rightNumber;
  }

  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const htmlToPlainText = (html) => {
  if (!html || typeof html !== "string") return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizeHtml(html), "text/html");
  return doc.body.textContent.replace(/\s+/g, " ").trim();
};

const getSummaryCopyText = ({ profile, summarySections, summaryText }) => {
  if (Array.isArray(summarySections) && summarySections.length > 0) {
    return summarySections
      .map((section) =>
        [section.title, ...(section.paragraphs || [])].filter(Boolean).join("\n"),
      )
      .filter(Boolean)
      .join("\n\n");
  }

  return [profile?.title, htmlToPlainText(summaryText)].filter(Boolean).join("\n\n");
};

const escapeTsvCell = (value) =>
  formatCellValue(value).replace(/\t/g, " ").replace(/\r?\n/g, " ");

const getTableCopyText = (
  rows,
  columns,
  prompt = "",
  columnLabels = {},
) => {
  if (!Array.isArray(rows) || !Array.isArray(columns) || columns.length === 0) {
    return "";
  }

  const header = columns
    .map((column) => escapeTsvCell(columnLabels[column] || column))
    .join("\t");
  const body = rows
    .map((row) => columns.map((column) => escapeTsvCell(row?.[column])).join("\t"))
    .join("\n");
  const data = [header, body].filter(Boolean).join("\n");
  const promptText = prompt ? `Prompt: ${prompt}\n` : "";

  return `${promptText}Data:\n${data}`;
};

const copyTextToClipboard = async (text) => {
  if (!text) return false;

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
};

const titleize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (letter) => letter.toUpperCase());

const getBreakdownRows = (breakdown) => {
  if (Array.isArray(breakdown)) return breakdown;
  if (Array.isArray(breakdown?.rows)) return breakdown.rows;
  return [];
};

const CSP_BREAKDOWN_CONFIG = {
  location: { title: "Location breakdown", labelHeader: "Location" },
  reason: { title: "Reason breakdown", labelHeader: "Reason" },
  origin: { title: "Origin breakdown", labelHeader: "Origin" },
};

const CSP_BREAKDOWN_KEYS = new Set(Object.keys(CSP_BREAKDOWN_CONFIG));

const getCspLabel = (row) =>
  row?.label ??
  row?.Label ??
  row?.location ??
  row?.Location ??
  row?.reason ??
  row?.Reason ??
  row?.origin ??
  row?.Origin ??
  "";

const isInvalidCspLabel = (label) =>
  /\b(test|dummy|sample|placeholder)\b/i.test(String(label || ""));

const getCspValue = (row) => row?.value ?? row?.Value ?? row?.idps ?? row?.IDPs;

const getCspShare = (row) =>
  row?.share_pct ??
  row?.sharePct ??
  row?.share ??
  row?.Share ??
  row?.["Share pct"];

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("en-US") : "";
};

const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";

  return `${Number.isInteger(number) ? number : number.toFixed(1).replace(/\.0$/, "")}%`;
};

const CSPProfileSummary = ({ annotatedSummary, profile }) => {
  const breakdownEntries = Object.entries(profile?.breakdowns || {}).filter(
    ([, breakdown]) =>
      getBreakdownRows(breakdown).some(
        (row) => !isInvalidCspLabel(getCspLabel(row)),
      ),
  );

  return (
    <div className={classes.csp}>
      {profile?.title && <h3 className={classes.csp_title}>{profile.title}</h3>}

      <div
        className={classes.summary}
        dangerouslySetInnerHTML={{ __html: annotatedSummary.html }}
      />

      {breakdownEntries.map(([key, breakdown]) => {
        const rows = getBreakdownRows(breakdown).filter(
          (row) => !isInvalidCspLabel(getCspLabel(row)),
        );
        const config = CSP_BREAKDOWN_CONFIG[key] || {
          title: breakdown?.title || `${titleize(key)} breakdown`,
          labelHeader: titleize(key),
        };
        const title = breakdown?.title || config.title;
        const isConfiguredCspBreakdown = CSP_BREAKDOWN_KEYS.has(key);

        return (
          <section key={key} className={classes.csp_section}>
            <h4 className={classes.csp_subtitle}>{title}</h4>
            {breakdown?.highlight && (
              <p className={classes.csp_highlight}>{breakdown.highlight}</p>
            )}
            <div className={classes.table_wrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>{config.labelHeader}</th>
                    <th>IDPs</th>
                    {!isConfiguredCspBreakdown && <th>Share</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={row.RowId || row.id || rowIndex}>
                      <td>{getCspLabel(row)}</td>
                      <td>{formatNumber(getCspValue(row))}</td>
                      {!isConfiguredCspBreakdown && (
                        <td>{formatPercent(getCspShare(row))}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
};

const LikeIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M7 10v10M7 10l4.7-6.2c.9-1.2 2.8-.5 2.8 1v4.1H19c1.2 0 2.1 1 1.9 2.2l-1.1 6.7c-.2 1.3-1.3 2.2-2.6 2.2H7M7 10H4v10h3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DislikeIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M7 14V4M7 14l4.7 6.2c.9 1.2 2.8.5 2.8-1v-4.1H19c1.2 0 2.1-1 1.9-2.2l-1.1-6.7C19.6 4.9 18.5 4 17.2 4H7M7 14H4V4h3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <rect
      x="9"
      y="9"
      width="10"
      height="10"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M6 15H5c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2v1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path
      d="m6 6 12 12M18 6 6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const WarningTriangleIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <path
      d="M12 3.5 21 19H3L12 3.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M12 8.5v5M12 16.7v.1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const getDataTableRowKey = (row, rowIndex) =>
  row?.RowId ?? row?.row_id ?? row?.id ?? rowIndex;

const OperationBreakdownTable = ({ rows }) => {
  const columns = getOperationBreakdownColumns(rows);

  if (!rows.length || !columns.length) {
    return (
      <div className={classes.operation_breakdown_empty}>
        No operation breakdown available.
      </div>
    );
  }

  return (
    <div className={classes.operation_breakdown_panel}>
      <table className={classes.operation_breakdown_table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <td key={column}>
                  {renderOperationBreakdownCell(column, row?.[column])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OperationBreakdownButton = ({
  expanded,
  onToggle,
  row,
  rowKey,
}) => {
  if (!hasOperationBreakdown(row)) return "";

  return (
    <button
      type="button"
      className={classes.operation_breakdown_button}
      aria-expanded={expanded}
      onClick={() => onToggle(rowKey)}
    >
      disaggregate
    </button>
  );
};

const DataTable = ({
  columnLabels = {},
  columns,
  expandedOperationRows = {},
  rows,
  expanded = false,
  onToggleOperationBreakdown,
  onSort,
  sortConfig,
}) => (
  <div
    className={`${classes.table_wrap}${
      expanded ? ` ${classes.table_wrap_expanded}` : ""
    }`}
  >
    <table
      className={`${classes.table}${expanded ? ` ${classes.table_expanded}` : ""}`}
    >
      <thead>
        <tr>
          {columns.map((column) => (
            column === OPERATION_BREAKDOWN_COLUMN ? (
              <th key={column}>
                <span className={classes.table_static_header}>
                  {columnLabels[column] || column}
                </span>
              </th>
            ) : (
            <th
              key={column}
              aria-sort={
                sortConfig.key === column
                  ? sortConfig.direction === "ascending"
                    ? "ascending"
                    : "descending"
                  : "none"
              }
            >
              <button
                type="button"
                className={classes.table_sort_button}
                onClick={() => onSort(column)}
              >
                <span>{columnLabels[column] || column}</span>
                <span className={classes.table_sort_indicator} aria-hidden="true">
                  {sortConfig.key === column
                    ? sortConfig.direction === "ascending"
                      ? "▲"
                      : "▼"
                    : "↕"}
                </span>
              </button>
            </th>
            )
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => {
          const rowKey = getDataTableRowKey(row, rowIndex);
          const isBreakdownExpanded = Boolean(expandedOperationRows[rowKey]);
          const breakdownRows = isBreakdownExpanded
            ? getOperationBreakdownRows(row)
            : [];

          return (
            <React.Fragment key={rowKey}>
              <tr>
                {columns.map((column) => (
                  <td key={column}>
                    {column === OPERATION_BREAKDOWN_COLUMN ? (
                      <OperationBreakdownButton
                        expanded={isBreakdownExpanded}
                        onToggle={onToggleOperationBreakdown}
                        row={row}
                        rowKey={rowKey}
                      />
                    ) : (
                      renderTableCell(
                        column,
                        row?.[column],
                        columnLabels[column],
                      )
                    )}
                  </td>
                ))}
              </tr>
              {isBreakdownExpanded && (
                <tr className={classes.operation_breakdown_row}>
                  <td colSpan={columns.length}>
                    <OperationBreakdownTable rows={breakdownRows} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
    <path
      d="M4 6h16M7 12h10M10 18h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const getFilterValue = (column, value) => {
  if (isFreshnessColumn(column)) {
    return getDataFreshness(value)?.label || formatCellValue(value);
  }

  return formatCellValue(value);
};

const FilterableDataTable = ({
  activeFilters,
  columnLabels = {},
  columns,
  expandedOperationRows = {},
  filterOptions,
  rows,
  expanded = false,
  onClearFilter,
  onSort,
  onToggleOperationBreakdown,
  onToggleFilter,
  sortConfig,
}) => {
  const [openFilterColumn, setOpenFilterColumn] = useState("");
  const tableRef = useRef(null);

  useEffect(() => {
    const closeFilterMenu = (event) => {
      if (!tableRef.current?.contains(event.target)) {
        setOpenFilterColumn("");
      }
    };

    document.addEventListener("mousedown", closeFilterMenu);
    return () => document.removeEventListener("mousedown", closeFilterMenu);
  }, []);

  if (!filterOptions) {
    return (
      <DataTable
        columnLabels={columnLabels}
        columns={columns}
        expandedOperationRows={expandedOperationRows}
        rows={rows}
        expanded={expanded}
        onToggleOperationBreakdown={onToggleOperationBreakdown}
        onSort={onSort}
        sortConfig={sortConfig}
      />
    );
  }

  return (
    <div
      ref={tableRef}
      className={`${classes.table_wrap}${
        expanded ? ` ${classes.table_wrap_expanded}` : ""
      }`}
    >
      <table
        className={`${classes.table}${expanded ? ` ${classes.table_expanded}` : ""}`}
      >
        <thead>
          <tr>
            {columns.map((column) => {
              const selectedValues = activeFilters[column] || [];
              const isFilterOpen = openFilterColumn === column;

              if (column === OPERATION_BREAKDOWN_COLUMN) {
                return (
                  <th key={column}>
                    <span className={classes.table_static_header}>
                      {columnLabels[column] || column}
                    </span>
                  </th>
                );
              }

              return (
                <th
                  key={column}
                  aria-sort={
                    sortConfig.key === column
                      ? sortConfig.direction === "ascending"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <div className={classes.table_header_controls}>
                    <button
                      type="button"
                      className={classes.table_sort_button}
                      onClick={() => onSort(column)}
                    >
                      <span>{columnLabels[column] || column}</span>
                      <span
                        className={classes.table_sort_indicator}
                        aria-hidden="true"
                      >
                        {sortConfig.key === column
                          ? sortConfig.direction === "ascending"
                            ? "▲"
                            : "▼"
                          : "↕"}
                      </span>
                    </button>
                    <div className={classes.table_filter_wrap}>
                      <button
                        type="button"
                        className={`${classes.table_filter_button}${
                          selectedValues.length
                            ? ` ${classes.table_filter_button_active}`
                            : ""
                        }`}
                        aria-label={`Filter ${columnLabels[column] || column}`}
                        aria-expanded={isFilterOpen}
                        onClick={() =>
                          setOpenFilterColumn((current) =>
                            current === column ? "" : column,
                          )
                        }
                      >
                        <FilterIcon />
                      </button>
                      {isFilterOpen && (
                        <div className={classes.table_filter_menu}>
                          <div className={classes.table_filter_menu_header}>
                            <span>
                              Filter {columnLabels[column] || column}
                            </span>
                            <button
                              type="button"
                              onClick={() => onClearFilter(column)}
                              disabled={!selectedValues.length}
                            >
                              Clear
                            </button>
                          </div>
                          <div className={classes.table_filter_options}>
                            {(filterOptions[column] || []).map((value) => (
                              <label
                                key={value}
                                className={classes.table_filter_option}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedValues.includes(value)}
                                  onChange={() => onToggleFilter(column, value)}
                                />
                                <span>{value || "(Blank)"}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const rowKey = getDataTableRowKey(row, rowIndex);
            const isBreakdownExpanded = Boolean(expandedOperationRows[rowKey]);
            const breakdownRows = isBreakdownExpanded
              ? getOperationBreakdownRows(row)
              : [];

            return (
              <React.Fragment key={rowKey}>
                <tr>
                  {columns.map((column) => (
                    <td key={column}>
                      {column === OPERATION_BREAKDOWN_COLUMN ? (
                        <OperationBreakdownButton
                          expanded={isBreakdownExpanded}
                          onToggle={onToggleOperationBreakdown}
                          row={row}
                          rowKey={rowKey}
                        />
                      ) : (
                        renderTableCell(
                          column,
                          row?.[column],
                          columnLabels[column],
                        )
                      )}
                    </td>
                  ))}
                </tr>
                {isBreakdownExpanded && (
                  <tr className={classes.operation_breakdown_row}>
                    <td colSpan={columns.length}>
                      <OperationBreakdownTable rows={breakdownRows} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const formatResponseLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const getAnalysisTypeLabel = (type) => {
  const labels = {
    ANC: "COMPARISION ANALYSIS",
    ANT: "TREND ANALYSIS",
    ANV: "AGERAGE ANALYSIS",
  };

  return labels[String(type || "").toUpperCase()] || "ANALYSIS";
};

const SNAPSHOT_TYPE_LABELS = {
  latest: "LATEST",
  latest_snapshot: "LATEST",
  yearly_latest: "YEARLY LATEST",
  year_lates: "YEARLY LATEST",
  round: "ROUND",
  reported_round: "ROUND",
  ranking: "RANKING",
};

const getSnapshotTypeKey = (analysisMeta) => {
  if (!analysisMeta) return "";

  if (typeof analysisMeta === "string") {
    return analysisMeta.toLowerCase();
  }

  const subtype =
    analysisMeta.type ||
    analysisMeta.key ||
    analysisMeta.snapshot_type ||
    analysisMeta.snapshotType ||
    analysisMeta.mode;
  const normalizedSubtype = String(subtype || "").toLowerCase();

  if (SNAPSHOT_TYPE_LABELS[normalizedSubtype]) {
    return normalizedSubtype;
  }

  return (
    Object.keys(analysisMeta).find(
      (key) =>
        SNAPSHOT_TYPE_LABELS[key.toLowerCase()] &&
        analysisMeta[key] !== false &&
        analysisMeta[key] !== null &&
        analysisMeta[key] !== undefined,
    ) || ""
  ).toLowerCase();
};

const getSnapshotTypeLabel = (analysisMeta) => {
  const snapshotTypeKey = getSnapshotTypeKey(analysisMeta);
  return SNAPSHOT_TYPE_LABELS[snapshotTypeKey] || "";
};

const ResponseType = ({ response }) => {
  const [isMethodologyOpen, setIsMethodologyOpen] = useState(false);
  const responseType = String(response?.response_type || "").toLowerCase();
  const analysisMeta = response?.response_type_analysis || {};

  if (!responseType) return null;

  if (responseType === "analysis") {
    const granularity =
      analysisMeta.granularity || analysisMeta.granuility || "";

    return (
      <div className={classes.response_type_wrapper}>
        <div className={classes.response_type}>
          <span>{getAnalysisTypeLabel(analysisMeta.type)}</span>
          {granularity && (
            <>
              <span className={classes.response_type_dot} aria-hidden="true" />
              <span>{formatResponseLabel(granularity)}</span>
            </>
          )}
          <span className={classes.response_type_info} aria-hidden="true">
            i
          </span>
        </div>
      </div>
    );
  }

  if (responseType === "snapshot") {
    const snapshotTypeKey = getSnapshotTypeKey(analysisMeta);
    const snapshotTypeLabel = getSnapshotTypeLabel(analysisMeta);
    const methodologyNote = response?.ranking_metadata?.methodology_note;
    const methodologyContent =
      typeof methodologyNote === "string"
        ? methodologyNote
        : methodologyNote?.content || "";
    const canToggleMethodology =
      snapshotTypeKey === "ranking" && !!methodologyContent;

    const responseTypeContent = (
      <>
        <span>SNAPSHOT ANALYSIS</span>
        {snapshotTypeLabel && (
          <>
            <span className={classes.response_type_dot} aria-hidden="true" />
            <span>{snapshotTypeLabel}</span>
          </>
        )}
        <span className={classes.response_type_info} aria-hidden="true">
          i
        </span>
      </>
    );

    return (
      <div className={classes.response_type_wrapper}>
        {canToggleMethodology ? (
          <button
            type="button"
            className={`${classes.response_type} ${classes.response_type_button}`}
            aria-expanded={isMethodologyOpen}
            onClick={() => setIsMethodologyOpen((current) => !current)}
          >
            {responseTypeContent}
          </button>
        ) : (
          <div className={classes.response_type}>{responseTypeContent}</div>
        )}
        {canToggleMethodology && isMethodologyOpen && (
          <div className={classes.response_type_note}>{methodologyContent}</div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.response_type_wrapper}>
      <div className={classes.response_type}>
        <span>{formatResponseLabel(responseType)}</span>
        <span className={classes.response_type_info} aria-hidden="true">
          i
        </span>
      </div>
    </div>
  );
};

const NoDataAdvisorCard = ({ advisor }) => {
  if (!advisor) {
    return null;
  }

  const title = advisor.title || "No exact match found";
  const body =
    advisor.body || advisor.content || advisor.message || advisor.summary || "";

  return (
    <div className={classes.no_data_advisor_card} role="status">
      <div className={classes.no_data_advisor_icon} aria-hidden="true">
        !
      </div>
      <div className={classes.no_data_advisor_content}>
        {title && (
          <div className={classes.no_data_advisor_title}>{title}</div>
        )}
        {body && (
          <div className={classes.no_data_advisor_message}>
            {body}
          </div>
        )}
        {advisor.user_instruction && (
          <div className={classes.no_data_advisor_instruction}>
            {advisor.user_instruction}
          </div>
        )}
      </div>
    </div>
  );
};

const DataGapAdvisoryCard = ({ response, onSuggestionClick }) => {
  if (!hasDataGapAdvisory(response)) return null;

  const message = getDataGapMessage(response);
  const suggestions = getDataGapSuggestions(response);

  return (
    <div className={classes.no_data_advisor_card} role="status">
      <div className={classes.no_data_advisor_icon} aria-hidden="true">
        !
      </div>
      <div className={classes.no_data_advisor_content}>
        <div className={classes.no_data_advisor_title}>No exact match found</div>
        {message && (
          <div className={classes.no_data_advisor_message}>{message}</div>
        )}
        {suggestions.length > 0 && (
          <div className={classes.data_gap_suggestions}>
            <div className={classes.data_gap_suggestions_label}>Try:</div>
            <div className={classes.data_gap_suggestion_list}>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id || suggestion.query_text}
                  type="button"
                  className={classes.data_gap_suggestion_button}
                  aria-label={`Run suggested query: ${suggestion.label}`}
                  onClick={() => onSuggestionClick?.(suggestion)}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const ResponseStatusCard = ({ response, status }) => {
  if (!status) return null;

  const type = getStatusType(status);
  const title = getStatusTitle(status);
  const body = getStatusBody(response, status);
  const bodyHtml = sanitizeHtml(body);

  if (!body && !title) return null;

  if (type === "normal") {
    return (
      <div className={classes.status_message_plain} role="status">
        {title && <div className={classes.status_title}>{title}</div>}
        {body && (
          <div
            className={classes.status_body}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        )}
      </div>
    );
  }

  const icon = type === "info" ? "i" : type === "warning" ? "!" : "!";
  const typeClass =
    type === "error"
      ? classes.status_card_error
      : type === "warning"
        ? classes.status_card_warning
        : classes.status_card_info;
  const iconClass =
    type === "error"
      ? classes.status_icon_error
      : type === "warning"
        ? classes.status_icon_warning
        : classes.status_icon_info;

  return (
    <div className={`${classes.status_card} ${typeClass}`} role="status">
      <span className={`${classes.status_icon} ${iconClass}`} aria-hidden="true">
        {icon}
      </span>
      <div className={classes.status_content}>
        {title && <div className={classes.status_title}>{title}</div>}
        {body && (
          <div
            className={classes.status_body}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        )}
      </div>
    </div>
  );
};

const SummaryStatusWarning = ({ reason }) => {
  const message =
    typeof reason === "string"
      ? reason
      : reason
        ? JSON.stringify(reason)
        : "A summary could not be generated for this result.";

  return (
    <div
      className={`${classes.status_card} ${classes.status_card_warning} ${classes.summary_status_warning}`}
      role="alert"
    >
      <span
        className={`${classes.status_icon} ${classes.status_icon_warning}`}
        aria-hidden="true"
      >
        !
      </span>
      <div className={classes.status_content}>
        {/*<div className={classes.status_title}>Summary unavailable</div>*/}
        <div className={classes.status_body}>{message}</div>
      </div>
    </div>
  );
};

const DataStatusInfo = ({ message }) => {
  if (!message) return null;

  return (
    <div
      className={`${classes.status_card} ${classes.status_card_info} ${classes.data_status_info}`}
      role="status"
    >
      <span
        className={`${classes.status_icon} ${classes.status_icon_info}`}
        aria-hidden="true"
      >
        i
      </span>
      <div className={classes.status_content}>
        <div className={classes.status_title}>Partial data</div>
        <div className={classes.status_body}>{message}</div>
      </div>
    </div>
  );
};

const MultiYearSummaryNotice = ({ response }) => {
  if (response?.summary_metadata?.multi_year !== true) return null;

  return (
    <div
      className={`${classes.status_card} ${classes.status_card_info} ${classes.analysis_multi_year_notice}`}
      role="status"
    >
      <span
        className={`${classes.status_icon} ${classes.status_icon_info}`}
        aria-hidden="true"
      >
        i
      </span>
      <div className={classes.status_content}>
        <div className={classes.status_body}>
          This summary uses data from multiple years. Please consider the time span when interpreting the results.
        </div>
      </div>
    </div>
  );
};

const VisualizationPanel = ({ response, meta, spec, status, error }) => {
  if (getVisualizationEligibility(response).eligible) {
    return <GenericQueryResultVisualization response={response} />;
  }

  const message =
    error?.message ||
    (status === "not_available"
      ? "No visualization is available for this result."
      : "Failed to generate visualization.");

  if (status === "loading") {
    return (
      <div className={classes.visualization_state} role="status">
        Generating visualization...
      </div>
    );
  }

  if (status === "success" && spec) {
    return (
      <div className={classes.visualization_panel}>
       
        {meta && (
          <div className={classes.visualization_meta}>
            {meta.source && <span>Source: {meta.source}</span>}
            {Number.isFinite(Number(meta.row_count)) && (
              <span>Rows: {Number(meta.row_count).toLocaleString("en-US")}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  if (status === "not_available") {
    return (
      <div className={classes.visualization_state} role="status">
        {message}
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className={`${classes.visualization_state} ${classes.visualization_state_error}`}
        role="alert"
      >
        {message}
      </div>
    );
  }

  return (
    <div className={classes.visualization_state} role="status">
      Visualization is available. Open the tab to generate it.
    </div>
  );
};

const SummarySections = ({ chart, sections = [] }) => {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  return (
    <div className={classes.summary_sections}>
      {sections.map((section, sectionIndex) => (
        <section
          key={section.id || section.title || sectionIndex}
          className={classes.summary_section}
        >
          {section.title && <h3>{section.title}</h3>}
          {section.paragraphs?.map((paragraph, paragraphIndex) => (
            <p
              key={paragraphIndex}
              dangerouslySetInnerHTML={{ __html: paragraph }}
            />
          ))}
          {section.id === "overview" && <TrendLineChart chart={chart} />}
        </section>
      ))}
    </div>
  );
};

// Detects whether a question targets male/female IDPs so the map's population
// toggle can match it on visualize. "female" is checked first since it contains
// the substring "male".
const detectPopulationType = (text) => {
  const value = (text || "").toLowerCase();
  if (value.includes("female")) return "female";
  if (value.includes("male")) return "male";
  return "total";
};

const AIResponse = ({
  onLoadVisualization,
  onPromptSubmit,
  onSuggestionClick,
  promptId = null,
  prompt = "",
  response = null,
  vizError = null,
  vizMeta = null,
  vizSpec = null,
  vizStatus = "idle",
}) => {
  const [activeTab, setActiveTab] = useState(() =>
    response?.summary_status?.generated === false ? "data" : "summary",
  );
  const [feedback, setFeedback] = useState("");
  const [copiedTarget, setCopiedTarget] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [isDataExpanded, setIsDataExpanded] = useState(false);
  const [summaryFigureMenu, setSummaryFigureMenu] = useState(null);
  const [activeCitationOrder, setActiveCitationOrder] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [expandedOperationRows, setExpandedOperationRows] = useState({});
  const responseRef = useRef(null);
  const summaryFigureMenuRef = useRef(null);
  const dispatch = useDispatch();
  const selectedCountry = useSelector((state) => state.selection.country);
  const selectedCountryPcode = useSelector(
    (state) => state.selection.countrypcode,
  );
  const selectedAdmin1 = useSelector((state) => state.selection.admin1);
  const selectedAdmin2 = useSelector((state) => state.selection.admin2);
  const selectedYear = useSelector((state) => state.selection.year);
  const selectedCause = useSelector((state) => state.selection.cause);
  const selectedOperation = useSelector((state) => state.selection.operation);
  const status = useMemo(() => getResponseStatus(response), [response]);
  const statusType = getStatusType(status);
  const dataStatus = response?.data_status || null;
  const isDataIncomplete = dataStatus?.complete === false;
  const summaryStatus = response?.summary_status || null;
  const isSummaryGenerationFailed = summaryStatus?.generated === false;
  const summaryText = getResponseSummaryText(response);
  const explicitRoundClarification = response?.explicit_round_clarification;
  const showExplicitRoundClarification = Boolean(explicitRoundClarification);
  const showSpecificRoundNoDataWarning =
    isSpecificRoundSnapshotNoData(response);
  const selectAmbiguousRoundOperation = useCallback(
    (option) => {
      const nextPrompt = buildRoundSnapshotOptionPrompt(
        prompt,
        option?.operation,
        option?.year,
      );
      if (!nextPrompt) return;

      if (option?.roundAnchorReportingDate) {
        onPromptSubmit?.(nextPrompt, {
          roundAnchorReportingDate: option.roundAnchorReportingDate,
        });
      } else {
        onPromptSubmit?.(nextPrompt);
      }
    },
    [onPromptSubmit, prompt],
  );
  const submitExplicitRoundClarificationQuery = useCallback(
    (actionQuery, explicitRoundAnchorContext) => {
      const query = String(actionQuery || "").trim();
      if (!query) return;

      if (explicitRoundAnchorContext !== undefined) {
        onPromptSubmit?.(query, { explicitRoundAnchorContext });
      } else {
        onPromptSubmit?.(query);
      }
    },
    [onPromptSubmit],
  );
  const isExplicitRound = isExplicitRoundResponse(response);
  const explicitRoundSelectedRounds = useMemo(
    () => getExplicitRoundSelectedRounds(response),
    [response],
  );
  const explicitRoundMissingRequestedRounds = useMemo(
    () => getExplicitRoundMissingRequestedRounds(response),
    [response],
  );
  const explicitRoundMissingWarning = explicitRoundMissingRequestedRounds.length
    ? `Requested round(s) not found: ${explicitRoundMissingRequestedRounds.map(formatMissingRound).join(", ")}. No substitute round was used.`
    : "";

  const queryResult = useMemo(
    () => isExplicitRound ? getExplicitRoundResultRows(response) : getResponseDataRows(response),
    [isExplicitRound, response],
  );
  const responseType = String(response?.response_type || "").toLowerCase();
  const overviewPayload =
    response?.response_type === "global_overview"
      ? response?.global_overview
      : response?.response_type === "regional_overview"
        ? response?.regional_overview
        : null;
  const isOverviewResponse = Boolean(overviewPayload);
  const isCountryProfileResponse =
    responseType === "country_profile" ||
    String(response?.route || "").toUpperCase() === "CSP" ||
    !!response?.profile;
  const hasCountryProfileResult =
    isCountryProfileResponse && (summaryText || response?.profile);
  const isSuccess =
    !status || status?.state === "success" || hasCountryProfileResult;
  const isCspResponse =
    String(response?.route || "").toUpperCase() === "CSP" && response?.profile;
  const hasDataRows = queryResult.length > 0;
  const queryResultColumns = response?.query_result_columns;
  const dataColumns = useMemo(
    () => getQueryResultColumnKeys(queryResult, queryResultColumns),
    [queryResult, queryResultColumns],
  );
  const hasOperationDataColumn = useMemo(
    () => queryResult.some((row) => hasOperationBreakdown(row)),
    [queryResult],
  );
  const columns = useMemo(
    () =>
      hasOperationDataColumn
        ? [...dataColumns, OPERATION_BREAKDOWN_COLUMN]
        : dataColumns,
    [dataColumns, hasOperationDataColumn],
  );
  const columnLabels = useMemo(
    () => ({
      ...getQueryResultColumnLabels(queryResultColumns),
      [OPERATION_BREAKDOWN_COLUMN]: OPERATION_BREAKDOWN_LABEL,
    }),
    [queryResultColumns],
  );
  const filterOptions = useMemo(
    () =>
      dataColumns.reduce((options, column) => {
        options[column] = Array.from(
          new Set(
            queryResult.map((row) => getFilterValue(column, row?.[column])),
          ),
        ).sort(compareTableValues);
        return options;
      }, {}),
    [dataColumns, queryResult],
  );
  const filteredQueryResult = useMemo(
    () =>
      queryResult.filter((row) =>
        Object.entries(activeFilters).every(([column, selectedValues]) => {
          if (!selectedValues.length) return true;
          return selectedValues.includes(
            getFilterValue(column, row?.[column]),
          );
        }),
      ),
    [activeFilters, queryResult],
  );
  const sortedQueryResult = useMemo(() => {
    if (!sortConfig.key) return filteredQueryResult;

    const direction = sortConfig.direction === "ascending" ? 1 : -1;
    return filteredQueryResult
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const comparison = compareTableValues(
          left.row?.[sortConfig.key],
          right.row?.[sortConfig.key],
        );
        return comparison === 0
          ? left.index - right.index
          : comparison * direction;
      })
      .map(({ row }) => row);
  }, [filteredQueryResult, sortConfig]);
  const summarySupportTables = response?.summary_support_tables || null;
  // A support row's own round data may omit country even though the
  // response is clearly about a known country. Resolve that fallback from
  // THIS response's own data only (see getResponseCountryFallback) — never
  // from the globally-selected map country, which can be stale leftover
  // from a previous response/interaction.
  const summaryCountryFallback = getResponseCountryFallback(summarySupportTables, queryResult);
  const annotatedSummary = useMemo(
    () => annotateSummary(summaryText, queryResult, summarySupportTables, summaryCountryFallback),
    [summaryText, queryResult, summarySupportTables, summaryCountryFallback],
  );
  const summaryTable = getSummaryTable(response);
  const noDataAdvisor = getNoDataAdvisory(response);
  const showNoDataAdvisor = hasNoDataAdvisory(response);
  const showDataGapAdvisory = hasDataGapAdvisory(response);
  const overviewTrendChart = response?.visualizations?.overview_trend_chart;
  const summarySections = Array.isArray(response?.summary_sections)
    ? response.summary_sections
    : [];
  const annotatedSummarySections = useMemo(
    () =>
      annotateSummarySections(
        summarySections,
        queryResult,
        summarySupportTables,
        summaryCountryFallback,
      ),
    [summarySections, queryResult, summarySupportTables, summaryCountryFallback],
  );
  const citationSources = annotatedSummarySections.sources.length > 0
    ? annotatedSummarySections.sources
    : annotatedSummary.sources;
  const citationSourceByRowId = useMemo(
    () =>
      citationSources.reduce((sourcesByRowId, source) => {
        sourcesByRowId.set(String(source.rowId), source);
        return sourcesByRowId;
      }, new Map()),
    [citationSources],
  );
  const visualizationSpec = getVisualizationSpec(response);
  const resolvedVisualizationSpec = vizSpec || visualizationSpec;
  const messagePromptId = promptId ?? response?.prompt_id ?? response?.promptId;
  const visualizationEligibility = useMemo(
    () => getVisualizationEligibility(response),
    [response],
  );
  const hideVisualizationTab = shouldHideVisualizationTab(response);
  const showSummary = showExplicitRoundClarification
    ? true
    : showDataGapAdvisory
      ? true
      : isSummaryGenerationFailed
      ? false
      : isSuccess
        ? true
        : status?.ui?.show_summary !== false;
  const showDataTab = showExplicitRoundClarification
    ? false
    : isExplicitRound
    ? true
    : isSummaryGenerationFailed
      ? true
      : isSuccess
        ? hasDataRows
        : status?.ui?.show_data_tab === true && hasDataRows;
  const showVisualizationTab =
    !showExplicitRoundClarification &&
    isSuccess &&
    visualizationEligibility.eligible &&
    !hideVisualizationTab;
  const hideResponseChrome = isExplicitRound
    ? false
    : showDataGapAdvisory ||
      !status ||
      !isSuccess ||
      HIDE_CHROME_STATUS_VALUES.has(String(status?.state || "").toUpperCase()) ||
      HIDE_CHROME_STATUS_VALUES.has(String(status?.code || "").toUpperCase());
  const useFlatStatusLayout =
    hideResponseChrome && !isSummaryGenerationFailed && activeTab === "summary";
  const showResponseTabs =
    !showExplicitRoundClarification &&
    !hideResponseChrome &&
    !isSummaryGenerationFailed &&
    (showSummary || showDataTab || showVisualizationTab);
  const summaryCopyText = useMemo(
    () => {
      if (isSummaryGenerationFailed) return "";

      return getSummaryCopyText({
        profile: response?.profile,
        summarySections,
        summaryText,
      });
    },
    [
      isSummaryGenerationFailed,
      response?.profile,
      summarySections,
      summaryText,
    ],
  );
  const tableCopyText = useMemo(
    () =>
      getTableCopyText(
        sortedQueryResult,
        dataColumns,
        prompt,
        columnLabels,
      ),
    [columnLabels, dataColumns, prompt, sortedQueryResult],
  );
  const sortTableBy = (column) => {
    if (column === OPERATION_BREAKDOWN_COLUMN) return;

    setSortConfig((current) => ({
      key: column,
      direction:
        current.key === column && current.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };
  const toggleTableFilter = (column, value) => {
    setActiveFilters((current) => {
      const selectedValues = current[column] || [];
      const nextValues = selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value];

      if (!nextValues.length) {
        const nextFilters = { ...current };
        delete nextFilters[column];
        return nextFilters;
      }

      return {
        ...current,
        [column]: nextValues,
      };
    });
  };
  const toggleOperationBreakdown = (rowKey) => {
    setExpandedOperationRows((current) => ({
      ...current,
      [rowKey]: !current[rowKey],
    }));
  };

  useEffect(() => {
    setExpandedOperationRows({});
  }, [queryResult]);
  const clearTableFilter = (column) => {
    setActiveFilters((current) => {
      const nextFilters = { ...current };
      delete nextFilters[column];
      return nextFilters;
    });
  };
  const visualizeSourceRow = useCallback((source) => {
    if (!source?.row) return;

    const sourceRow = source.row;
    const operationData = extractOperationMeta(sourceRow);
    const countryIso3 =
      operationData.iso3 ||
      sourceRow.iso3 ||
      sourceRow.admin_0_pcode ||
      getIso3ByCountryName(sourceRow.admin_0_name);
    const rowYear = Number(
      sourceRow.year ?? sourceRow.Year ?? sourceRow.YEAR,
    );
    const rowCause =
      sourceRow.reason_for_displacement ||
      sourceRow.reason ||
      sourceRow.cause ||
      "All";
    const targetCountry = sourceRow.admin_0_name;
    const targetPcode = countryIso3 || "";
    // Match handleVisualizeDataClick's precedence exactly: the nested
    // operation_data/operation_detail JSON is authoritative, the flat
    // field on the row is only a fallback.
    const targetOperation =
      operationData.operation || sourceRow.operation || "All";
    const targetRound = operationData.round ?? null;

    const populationType = detectPopulationType(prompt || summaryCopyText);

    // Copied directly from the working handleVisualizeDataClick dispatch
    // sequence (AIResponseSource.js) rather than sharing a helper.
    const mapWillReload =
      selectedCountry !== targetCountry ||
      selectedCountryPcode !== targetPcode ||
      selectedAdmin1 !== "" ||
      selectedAdmin2 !== "" ||
      (Number.isFinite(rowYear) && Number(selectedYear) !== rowYear) ||
      selectedCause !== rowCause ||
      selectedOperation !== targetOperation;

    if (mapWillReload) {
      dispatch(selectionsSliceActions.setMapLoading(true));
    }
    dispatch(selectionsSliceActions.setCountry(targetCountry));
    dispatch(selectionsSliceActions.setCountryPcode(targetPcode));
    dispatch(selectionsSliceActions.setAdmin1(""));
    dispatch(selectionsSliceActions.setAdmin2(""));
    if (Number.isFinite(rowYear)) {
      dispatch(selectionsSliceActions.setYear(rowYear));
    }
    dispatch(selectionsSliceActions.setCause(rowCause));
    dispatch(selectionsSliceActions.setOperation(targetOperation));
    dispatch(selectionsSliceActions.setRound(targetRound));
    dispatch(selectionsSliceActions.setRoundFilter(targetRound));
    dispatch(selectionsSliceActions.setPopulationType(populationType));
    setSummaryFigureMenu(null);
  }, [
    dispatch,
    prompt,
    selectedAdmin1,
    selectedAdmin2,
    selectedCause,
    selectedCountry,
    selectedCountryPcode,
    selectedOperation,
    selectedYear,
    summaryCopyText,
  ]);
  const selectSummarySourceRound = useCallback((summaryRowId, sourceOperationRound) => {
    if (!summaryRowId || !sourceOperationRound) return;

    dispatch(
      selectionsSliceActions.setSelectedSummarySourceRound({
        summaryRowId,
        sourceOperationRound,
      }),
    );
  }, [dispatch]);
  const copyToClipboard = async (target, text) => {
    const copied = await copyTextToClipboard(text);

    if (!copied) return;

    setCopiedTarget(target);
    window.setTimeout(() => {
      setCopiedTarget((current) => (current === target ? "" : current));
    }, 1600);
  };
  const reportPromptId = Number(
    promptId ?? response?.prompt_id ?? response?.promptId,
  );
  // A response can only be reported when /api/chat handed us a valid prompt id.
  const canReport = Number.isInteger(reportPromptId) && reportPromptId > 0;
  const reportIssueButton = (
    <button
      type="button"
      className={`${classes.report_button} ${classes.error_report_button} ${
        classes[`error_report_button_${statusType}`] ||
        classes.error_report_button_error
      }`}
      aria-label="Report issue"
      onClick={() => setReportOpen(true)}
    >
      <WarningTriangleIcon />
      Report issue
    </button>
  );

  const handleReportSubmit = async ({ response_rating, remark }) => {
    if (!canReport) {
      console.error("Cannot report response: missing prompt id.");
      return false;
    }

    setReportSubmitting(true);
    try {
      const trimmedRemark = remark?.trim();
      const res = await fetch(apiUrl("/api/updatePrompt"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt_id: Number(reportPromptId),
          response_rating: Number(response_rating),
          remark: trimmedRemark ? trimmedRemark : null,
        }),
      });
      if (!res.ok) {
        throw new Error(`Report failed: ${res.status}`);
      }
      return true;
    } catch (error) {
      console.error("Failed to submit response report:", error);
      return false;
    } finally {
      setReportSubmitting(false);
    }
  };

  const clearSourceHighlight = () => {
    setActiveCitationOrder(null);
    responseRef.current
      ?.querySelectorAll(
        `[data-freshness-highlight], .${classes.citation_figure_highlight}`,
      )
      .forEach((node) => {
        node.classList.remove(classes.citation_figure_highlight);
        node.removeAttribute("data-freshness-highlight");
      });
  };
  const highlightSourceFigure = (sourceOrder, rowId, freshnessColor) => {
    clearSourceHighlight();
    setActiveCitationOrder(sourceOrder ? String(sourceOrder) : null);

    const responseElement = responseRef.current;
    if (!responseElement) return;

    const citedFigures = Array.from(
      responseElement.querySelectorAll("[data-citation-order]"),
    )
      .filter((node) =>
        (node.getAttribute("data-citation-order") || "")
          .split(",")
          .includes(String(sourceOrder)),
      )
      .map((citation) =>
        citation.closest("[data-data-row-id], [data-summary-row-id]"),
      )
      .filter(Boolean);

    if (citedFigures.length === 0) {
      const fallbackFigure = Array.from(
        responseElement.querySelectorAll("[data-data-row-id], [data-summary-row-id]"),
      ).find(
        (node) => getCitationTargetRowId(node) === String(rowId),
      );
      if (fallbackFigure) citedFigures.push(fallbackFigure);
    }

    citedFigures.forEach((citedFigure) => {
      citedFigure.classList.add(
        classes.citation_figure,
        classes.citation_figure_highlight,
      );
      citedFigure.setAttribute(
        "data-freshness-highlight",
        freshnessColor || "gray",
      );
    });
  };

  useEffect(() => {
    const responseElement = responseRef.current;
    if (!responseElement) return undefined;

    const getCitationTarget = (eventTarget) =>
      eventTarget?.closest?.("[data-data-row-id], [data-summary-row-id]");
    const openCitationMenu = (target) => {
      const rowId = getCitationTargetRowId(target);
      const source = citationSourceByRowId.get(String(rowId));

      const rect = target.getBoundingClientRect();
      const menuHeight = 48;
      const openUp = window.innerHeight - rect.bottom < menuHeight + 12;

      setSummaryFigureMenu({
        rowId: String(rowId),
        sourceType: source?.type || (target?.hasAttribute?.("data-summary-row-id") ? "summary_support" : "query_result"),
        unavailable: !source,
        x: Math.min(rect.left, window.innerWidth - 190),
        y: openUp ? rect.top - menuHeight - 6 : rect.bottom + 6,
      });
    };
    const handleClick = (event) => {
      const target = getCitationTarget(event.target);

      if (!target) return;

      event.preventDefault();
      openCitationMenu(target);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      const target = getCitationTarget(event.target);

      if (!target) return;

      event.preventDefault();
      openCitationMenu(target);
    };
    const activateCitationSource = (event) => {
      const target = getCitationTarget(event.target);
      if (!target) return;

      const source = citationSourceByRowId.get(String(getCitationTargetRowId(target)));
      setActiveCitationOrder(
        source?.orders && source.orders.length > 0 ? source.orders.join(",") : null,
      );
    };
    const deactivateCitationSource = (event) => {
      const target = getCitationTarget(event.target);
      if (!target || target.contains(event.relatedTarget)) return;

      setActiveCitationOrder(null);
    };

    responseElement.addEventListener("click", handleClick);
    responseElement.addEventListener("keydown", handleKeyDown);
    responseElement.addEventListener("mouseover", activateCitationSource);
    responseElement.addEventListener("focusin", activateCitationSource);
    responseElement.addEventListener("mouseout", deactivateCitationSource);
    responseElement.addEventListener("focusout", deactivateCitationSource);

    return () => {
      responseElement.removeEventListener("click", handleClick);
      responseElement.removeEventListener("keydown", handleKeyDown);
      responseElement.removeEventListener("mouseover", activateCitationSource);
      responseElement.removeEventListener("focusin", activateCitationSource);
      responseElement.removeEventListener("mouseout", deactivateCitationSource);
      responseElement.removeEventListener("focusout", deactivateCitationSource);
    };
  }, [citationSourceByRowId]);

  useEffect(() => {
    if (!summaryFigureMenu) return undefined;

    const closeMenu = (event) => {
      if (
        summaryFigureMenuRef.current?.contains(event.target) ||
        event.target?.closest?.("[data-data-row-id], [data-summary-row-id]")
      ) {
        return;
      }

      setSummaryFigureMenu(null);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSummaryFigureMenu(null);
    };

    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [summaryFigureMenu]);

  useEffect(() => {
    if (isSummaryGenerationFailed) {
      if (activeTab !== "data") setActiveTab("data");
      return;
    }

    if (hideResponseChrome && activeTab !== "summary") {
      setActiveTab("summary");
      return;
    }

    if (!isSuccess && activeTab !== "summary") {
      setActiveTab("summary");
      return;
    }

    if (activeTab === "data" && !showDataTab) {
      setActiveTab(showSummary ? "summary" : "viz");
      return;
    }

    if (activeTab === "viz" && !showVisualizationTab) {
      setActiveTab(showSummary ? "summary" : "data");
    }
  }, [
    activeTab,
    hideResponseChrome,
    isSuccess,
    isSummaryGenerationFailed,
    showDataTab,
    showSummary,
    showVisualizationTab,
  ]);

  useEffect(() => {
    const willLoad =
      activeTab === "viz" &&
      !visualizationEligibility.eligible &&
      showVisualizationTab &&
      !!messagePromptId &&
      (!vizStatus || vizStatus === "idle");

    /*
    console.log("[Viz UI] activeTab:", activeTab);
    console.log("[Viz UI] promptId:", messagePromptId);
    console.log("[Viz UI] vizStatus:", vizStatus);
    console.log("[Viz UI] willLoad:", willLoad);
    */
    if (willLoad) {
      onLoadVisualization?.();
    }
  }, [
    activeTab,
    messagePromptId,
    onLoadVisualization,
    showVisualizationTab,
    vizStatus,
    visualizationEligibility.eligible,
  ]);

  useEffect(() => {
    if (!isDataExpanded) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsDataExpanded(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isDataExpanded]);

  if (showExplicitRoundClarification) {
    return (
      <article
        ref={responseRef}
        className={`${classes.response} ${classes.response_flat_status}`}
      >
        <div className={classes.panel}>
          <ExplicitRoundClarificationCard
            clarification={explicitRoundClarification}
            onActionQuery={submitExplicitRoundClarificationQuery}
          />
        </div>
      </article>
    );
  }
  if (showSpecificRoundNoDataWarning) {
    return (
      <article
        ref={responseRef}
        className={`${classes.response} ${classes.response_flat_status}`}
      >
        <div className={classes.panel}>
          <NoDataAdvisorCard
            advisor={{
              title: "No exact match found",
              message: summaryText,
            }}
          />
        </div>
        <div className={classes.error_actions} aria-label="AI response actions">
          {reportIssueButton}
        </div>
        <ReportIssueModal
          open={reportOpen}
          submitting={reportSubmitting}
          onClose={() => setReportOpen(false)}
          onSubmit={handleReportSubmit}
        />
      </article>
    );
  }

  return (
    <article
      ref={responseRef}
      className={`${classes.response}${
        useFlatStatusLayout ? ` ${classes.response_flat_status}` : ""
      }`}
    >
      {showResponseTabs && (
        <div className={classes.response_topline}>
          <div className={classes.tabs} role="tablist" aria-label="AI response tabs">
            {showSummary && (
              <button
                type="button"
                className={`${classes.tab}${activeTab === "summary" ? ` ${classes.tab_active}` : ""}`}
                onClick={() => setActiveTab("summary")}
              >
                Summary
              </button>
            )}
            {showDataTab && (
              <button
                type="button"
                className={`${classes.tab}${activeTab === "data" ? ` ${classes.tab_active}` : ""}`}
                onClick={() => setActiveTab("data")}
              >
                Data
              </button>
            )}
            {showVisualizationTab && (
              <button
                type="button"
                className={`${classes.tab}${activeTab === "viz" ? ` ${classes.tab_active}` : ""}`}
                onClick={() => setActiveTab("viz")}
              >
                Visualization
              </button>
            )}
          </div>
          <ResponseType response={response} />
        </div>
      )}

      <div className={classes.panel}>
        {activeTab === "summary" &&
          showSummary &&
          (!isSummaryGenerationFailed || showExplicitRoundClarification) && (
          <>
            {showExplicitRoundClarification ? (
              <ExplicitRoundClarificationCard
                clarification={explicitRoundClarification}
                onActionQuery={submitExplicitRoundClarificationQuery}
              />
            ) : showDataGapAdvisory ? (
              <DataGapAdvisoryCard
                response={response}
                onSuggestionClick={onSuggestionClick}
              />
            ) : !isSuccess ? (
              showNoDataAdvisor ? (
                <NoDataAdvisorCard advisor={noDataAdvisor} />
              ) : (
                <ResponseStatusCard response={response} status={status} />
              )
            ) : (
              isOverviewResponse ? (
                <GlobalOverviewResponse overview={overviewPayload} />
              ) : isCspResponse ? (
                <CSPProfileSummary
                  annotatedSummary={annotatedSummary}
                  profile={response.profile}
                />
              ) : (
                summarySections.length > 0 ? (
                  <SummarySections
                    chart={overviewTrendChart}
                    sections={annotatedSummarySections.sections}
                  />
                ) : (
                  annotatedSummary.html && (
                  <>
                    <AmbiguousRoundSnapshotSummary
                      html={annotatedSummary.html}
                      onOperationClick={selectAmbiguousRoundOperation}
                    />
                    <TrendLineChart chart={overviewTrendChart} />
                  </>
                  )
                )
              )
            )}
            {isSuccess &&
              !showExplicitRoundClarification &&
              !showDataGapAdvisory &&
              !isOverviewResponse && <SummaryTable table={summaryTable} />}
            {isSuccess &&
              !showExplicitRoundClarification &&
              !showDataGapAdvisory && (
                <NoDataAdvisorCard advisor={noDataAdvisor} />
              )}
            <MultiYearSummaryNotice response={response} />
          </>
        )}

        {activeTab === "data" && showDataTab && (
          <div className={classes.data_panel}>
            {isDataIncomplete && <DataStatusInfo message={dataStatus?.message} />}
            {isSummaryGenerationFailed && (
              <div className={classes.summary_failure_block}>
                <SummaryStatusWarning reason={summaryStatus?.message} />
                <div
                  className={classes.summary_failure_actions}
                  aria-label="AI response actions"
                >
                  {reportIssueButton}
                </div>
              </div>
            )}

            {isExplicitRound && (
              <>
                {explicitRoundMissingWarning && (
                  <div className={`${classes.status_card} ${classes.status_card_warning}`} role="status">
                    {explicitRoundMissingWarning}
                  </div>
                )}
                <h4>Selected rounds</h4>
                <ExplicitRoundSelectedRoundsTable rows={explicitRoundSelectedRounds} />
                <h4>Result rows</h4>
                {!hasDataRows && <p>No result rows were returned.</p>}
              </>
            )}

            {hasDataRows && (
              <FilterableDataTable
                activeFilters={activeFilters}
                columnLabels={columnLabels}
                columns={columns}
                expandedOperationRows={expandedOperationRows}
                filterOptions={filterOptions}
                rows={sortedQueryResult}
                onClearFilter={clearTableFilter}
                onSort={sortTableBy}
                onToggleOperationBreakdown={toggleOperationBreakdown}
                onToggleFilter={toggleTableFilter}
                sortConfig={sortConfig}
              />
            )}
            <div className={classes.data_toolbar}>
              <div className={classes.data_summary}>
                <span>
                  {filteredQueryResult.length.toLocaleString("en-US")}
                  {filteredQueryResult.length !== queryResult.length
                    ? ` of ${queryResult.length.toLocaleString("en-US")}`
                    : ""}{" "}
                  records
                </span>
                <span>{dataColumns.length.toLocaleString("en-US")} indicators</span>
              </div>
              <div className={classes.data_toolbar_actions}>
                <button
                  type="button"
                  className={classes.copy_button}
                  onClick={() => copyToClipboard("table", tableCopyText)}
                  disabled={!tableCopyText}
                >
                  <CopyIcon />
                  {copiedTarget === "table" ? "Copied" : "Copy"}
                </button>
                <button
                  type="button"
                  className={classes.copy_button}
                  onClick={() => setIsDataExpanded(true)}
                  disabled={!hasDataRows}
                >
                  <ExpandIcon />
                  Expand
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "viz" && showVisualizationTab && (
          <VisualizationPanel
            response={response}
            error={vizError}
            meta={vizMeta}
            spec={resolvedVisualizationSpec}
            status={vizStatus}
          />
        )}
      </div>

      {!hideResponseChrome && (
        <AIResponseSource
          collapseSignal={activeTab !== "summary"}
          promptId={promptId ?? response?.prompt_id ?? response?.promptId}
          sources={citationSources}
          summarySupportTables={summarySupportTables}
          populationType={detectPopulationType(prompt || summaryCopyText)}
          activeSourceOrder={activeCitationOrder}
          onSourceHover={highlightSourceFigure}
          onSourceLeave={clearSourceHighlight}
        >
          <div className={classes.actions} aria-label="AI response actions">
            <button
              type="button"
              className={`${classes.icon_button}${copiedTarget === "summary" ? ` ${classes.icon_button_copied}` : ""}`}
              aria-label={copiedTarget === "summary" ? "Summary copied" : "Copy summary"}
              title={copiedTarget === "summary" ? "Copied" : "Copy summary"}
              onClick={() => copyToClipboard("summary", summaryCopyText)}
              disabled={!summaryCopyText}
            >
              {copiedTarget === "summary" ? <CheckIcon /> : <CopyIcon />}
            </button>
            <button
              type="button"
              className={`${classes.icon_button}${feedback === "like" ? ` ${classes.icon_button_active}` : ""}`}
              aria-label="Like response"
              onClick={() => setFeedback((current) => (current === "like" ? "" : "like"))}
            >
              <LikeIcon />
            </button>
            <button
              type="button"
              className={`${classes.icon_button}${feedback === "dislike" ? ` ${classes.icon_button_active}` : ""}`}
              aria-label="Dislike response"
              onClick={() => setFeedback((current) => (current === "dislike" ? "" : "dislike"))}
            >
              <DislikeIcon />
            </button>
            <button
              type="button"
              className={classes.report_button}
              aria-label="Report issue"
              onClick={() => setReportOpen(true)}
            >
              Report issue
            </button>
          </div>
        </AIResponseSource>
      )}

      {hideResponseChrome && !isSummaryGenerationFailed && !showDataGapAdvisory && (
        <div className={classes.error_actions} aria-label="AI response actions">
          {reportIssueButton}
        </div>
      )}

      {isDataExpanded &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={classes.data_modal_backdrop}
            role="presentation"
            onMouseDown={() => setIsDataExpanded(false)}
          >
            <section
              className={classes.data_modal}
              role="dialog"
              aria-modal="true"
              aria-label="Expanded response data"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header className={classes.data_modal_header}>
                <div>
                  <h3>Response data</h3>
                  <div className={classes.data_summary}>
                    <span>
                      {filteredQueryResult.length.toLocaleString("en-US")}
                      {filteredQueryResult.length !== queryResult.length
                        ? ` of ${queryResult.length.toLocaleString("en-US")}`
                        : ""}{" "}
                      records
                    </span>
                    <span>{dataColumns.length.toLocaleString("en-US")} indicators</span>
                  </div>
                </div>
                <div className={classes.data_toolbar_actions}>
                  <button
                    type="button"
                    className={classes.copy_button}
                    onClick={() => copyToClipboard("table", tableCopyText)}
                    disabled={!tableCopyText}
                  >
                    <CopyIcon />
                    {copiedTarget === "table" ? "Copied" : "Copy"}
                  </button>
                  <button
                    type="button"
                    className={classes.modal_close_button}
                    aria-label="Close expanded data"
                    title="Close"
                    onClick={() => setIsDataExpanded(false)}
                  >
                    <CloseIcon />
                  </button>
                </div>
              </header>
              <FilterableDataTable
                activeFilters={activeFilters}
                columnLabels={columnLabels}
                columns={columns}
                expandedOperationRows={expandedOperationRows}
                filterOptions={filterOptions}
                rows={sortedQueryResult}
                expanded
                onClearFilter={clearTableFilter}
                onSort={sortTableBy}
                onToggleOperationBreakdown={toggleOperationBreakdown}
                onToggleFilter={toggleTableFilter}
                sortConfig={sortConfig}
              />
            </section>
          </div>,
          document.body,
        )}

      {summaryFigureMenu &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={summaryFigureMenuRef}
            className={`${classes.summary_figure_menu}${
              summaryFigureMenu.sourceType === "summary_support"
                ? ` ${classes.summary_figure_menu_wide}`
                : ""
            }`}
            style={{
              left: `${summaryFigureMenu.x}px`,
              top: `${summaryFigureMenu.y}px`,
            }}
          >
            {summaryFigureMenu.sourceType === "summary_support" ? (
              <SummarySupportDetails
                source={citationSourceByRowId.get(String(summaryFigureMenu.rowId))}
                onOpen={selectSummarySourceRound}
                onRoundClick={selectSummarySourceRound}
                onClose={() => setSummaryFigureMenu(null)}
              />
            ) : summaryFigureMenu.unavailable ? (
              <div className={classes.summary_support_details}>
                Source details unavailable
              </div>
            ) : (
              <button
                type="button"
                className={classes.summary_figure_menu_item}
                onClick={() =>
                  visualizeSourceRow(
                    citationSourceByRowId.get(String(summaryFigureMenu.rowId)),
                  )
                }
              >
                Visualize data
              </button>
            )}
          </div>,
          document.body,
        )}

      <ReportIssueModal
        open={reportOpen}
        submitting={reportSubmitting}
        onClose={() => setReportOpen(false)}
        onSubmit={handleReportSubmit}
      />
    </article>
  );
};


export default AIResponse;
